import StoreMallDirectoryOutlinedIcon from "@material-ui/icons/StoreMallDirectoryOutlined";
import * as Sentry from "@sentry/browser";
import { IAddItem, IMutateAllocationBagValue, ISizeRange } from "actions/collections";
import {
    CellClickedEvent,
    CellValueChangedEvent,
    ColDef,
    ColGroupDef,
    Column,
    ColumnApi,
    ColumnGroup,
    ColumnGroupOpenedEvent,
    ColumnPinnedEvent,
    ColumnVisibleEvent,
    ColumnVO,
    DragStoppedEvent,
    ExcelStyle,
    GetMainMenuItems,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    ICellRendererParams,
    IDetailCellRendererParams,
    IServerSideGetRowsParams,
    ModelUpdatedEvent,
    RowNode,
    SideBarDef,
    SortChangedEvent,
    SortModelItem,
    ToolPanelVisibleChangedEvent,
    ValueGetterParams,
} from "ag-grid-community";
import "ag-grid-community/dist/styles/ag-grid.css";
import "ag-grid-community/dist/styles/ag-theme-balham.css";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import classNames from "classnames";
import regexes from "components/draft-js-mention/utils/hashtagRegex";
import {
    getUpdatedSizesCurveAndStatus,
    isDoor,
} from "components/product-collection/product-collection-item-allocation";
import {
    generateDateValueObject,
    IDate,
    isValidDateFormat,
} from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/editor-helper";
import {
    buildPathBasedOnType,
    getAssortmentTotalsRow,
    getServerRollupAssortmentTotalsRow,
    getTotalType,
    ICurrency,
    ITotalRowResponse,
    PlanMetricRows,
    validatePinnedRows,
} from "components/product-collection/product-collection-views/product-collection-grid-view/grid-renderers/pinned-row-renderers";
import {
    AllocationDimensionClipboardActions,
    AssortmentShoppingActions,
    DOOR_UNIT_CHANGED,
    GRID_CELL_CHANGE,
    Location,
} from "constants/analytics";
import {
    ALLOCATION_DIMENSION_ID_PREFIX,
    AUTOSIZE_COLUMNS,
    ColDefType,
    COLUMN_EVENT_SOURCES,
    Editors,
    EXCEL_STYLES,
    FILTER_KEY,
    GridParamType,
    IBuiltDoorTotals,
    IItemFieldValue,
    IMAGE_COLUMN_BUFFER,
    IMAGE_URL,
    Item,
    Item as ProductDisplayFieldPaths,
    ItemBagType,
    ItemFields,
    Kinds,
    MANUFACTURED_SIZES_DELIMITER,
    notePrefix,
    ROW_HEIGHT,
    SIZE_RANGE_DELIMITER,
    SizeCurveMethod,
    SizeCurveStatus,
    SizeTableTypes,
    SMU_FILTER,
    TILE_VIEW_ITEM_SIZES,
    ToolPanels,
    UI_COLUMN_DRAGGED,
    UnitsEntryType,
    ValidationModes,
    ValueTypes,
    View,
    AG_GRID_SCROLLABLE_EL_SELECTOR,
} from "constants/product-collection";
import { AssortmentEvents, AssortmentTransactions } from "constants/sentry-metrics";
import { IImageCellDimensions } from "constants/types";
import { MultipleChoiceCellEditorWrapper } from "containers/cell-editors/multiple-choice-cell-editor-container";
import { PackTypeCellEditorEditorWrapper } from "containers/cell-editors/pack-type-cell-editor-container";
import { GenericReduxConnectedRenderer } from "containers/cell-renderers/generic-redux-renderer";
import { DimensionsPanelReduxState } from "reducers/dimensions-panel/dimensions-panel";
import { DimensionsToolPanel } from "./tool-panels/dimensions-tool-panel/dimensions-tool-panel";
import { RollupType } from "containers/folders/types/server-rollups";
import { findOneFieldByKind } from "epics/collections/helpers/allocation";
import {
    getSortModelFromApi,
    handleFillOperation,
    isDateString,
    mapValuesToDateObj,
    resetSelectedCellRanges,
    suppressDeleteBackspaceKeyboard,
} from "helpers/ag-grid";
import {
    AllocationBag,
    AllocationBagType,
    AllocationDimensionIndex,
    BAG_VALUE_PARAMS_SEPARATOR,
    clearBagValues,
    explodeRowByAllocationDimensions,
    explodeRowByAllocationDimensionsLegacy,
    generateReconciliationBagMutations,
    getAllocationBagValue,
    getAllocationDimensionByIndex,
    getBagValuesByRowId,
    IAllocationDimension,
    IAllocationDimensionValue,
    isAllocationDimensionId,
    ROLLUP_ALLOCATION_DIMENSION_DELIMITER,
    syncAllocationsWithBulks,
    transformDoorAttributesFiltersForRollup,
} from "helpers/allocation-dimension";
import uuid from "uuid/v4";
import { DATATEST } from "constants/integration";
import {
    CollectionSavedViewRollupSeparator,
    DataViewPanel,
    FilterTypes,
    IFiltersQueryParam,
    IGridParamsQueryParam,
    IPreference,
    ISaveStateToGridQueryParamsActions,
    ISortOrder,
    ISortSortBy,
    SaveStateToGridQueryParamsType,
    ShopToolPanel,
    SMUToolPanel,
    SortToolPanel,
} from "./tool-panels";
import { ISuggestionItem, ISuggestionObject } from "containers/product-collections/product-collection-cart";
import { adjustDestinationIndex, getDataFromApi, getViewDataFromApi, IGridData } from "helpers/collection";
import { IPurchaseOrderDoorGroup } from "helpers/delivery-purchase-order";
import {
    DOOR_GROUP_QUERY_KEY,
    getAttributeFilters,
    getDimensionFilters,
    getGroupId,
    getVisibleDoors,
    IDoor,
    isDoorColumn,
    isDoorInClassification,
    legacy_parseDimensionParams,
} from "helpers/door-collection";
import { FeatureFlags } from "helpers/feature-flags";
import { metricsTimerEnd } from "helpers/metrics";
import { MetricsKind } from "helpers/metrics/types";
import { arrayMove } from "helpers/mutable";
import * as PropTypes from "prop-types";
import {
    ColumnOrderTypes,
    ColumnTypes,
    generateDefaultColumnDefs,
    generatePinnedRowId,
    getAttributesDoorsFilter,
    getColumnGroupsAtLevel,
    getDoorsWithinClasifications,
    getNewDimensionFilters,
    getNewResizedImageCellDimensions,
    getUpdatedSchemaKeysByMovingColumns,
    gridAggFunctions,
    groupSortComparator,
    handleTabToNextCell,
    isColumnGroupsExpanded,
    legacyDeriveSingleSizeAllocationsFromBulks,
    mergeAllocationDimensions,
} from "./product-collection-grid-view-helper";
import { nullish } from "helpers/nullish";
import { amphibiousGetIn, areAllItemsContainedInList, amphibiousToJS } from "helpers/objUtils";
import { shallowEqualAndExclude } from "helpers/performance/shallow-compare-exclude";
import {
    ARTIFICAL_ALLOCATION_DIMENSIONS_NODE,
    changeSizePackField,
    getBulkUnitsOverDimensions,
    getComparator,
    getFieldNameToSchemaMapping,
    getPinnedDto,
    getSizeRelatedFields,
    getSizesFieldsToUpdateFromSelectedSizes,
    getTileViewCellDimensions,
    getTotalWidthFromNoDimensionsNoPinnedVisibleColumns,
    IDisplayFields,
    getSortOrderForAutoGroupColumn,
    ISchemaKey,
    parseSelectedSizes,
    removeSortFromColumnStateList,
    sizeManufacturedGetter,
    DistributionTargets,
    getNewUpcValues,
} from "helpers/product-collection";
import {
    copyAndPasteUnits,
    CopyCutPasteActions,
    copyCutPasteMenuHandler,
    cutAndPasteUnits,
    IAllocationClipboardState,
    UnitsPath,
} from "helpers/product-collection/units-copy-paste";
import { buildExportToItemsToStatusMap } from "helpers/product-collection/validation-log";
import { buildServerRollupQuery, getDoorGroupDimensions } from "helpers/query-dsl-builder";
import { RollupTotalKind } from "helpers/query-dsl-types";
import {
    ASSORTMENT_FIELD_NAME,
    buildServerRollupRowNodeId,
    COL_DEF_SEPARATOR,
    COLLECTION_ID_FIELD_NAME,
    createColsHash,
    enhanceSecondaryColDefs,
    getBlankTextValue,
    getGroupByColumn,
    getOnlyDoorGroupedRows,
    getServerRollupEndpoint,
    IMAGE_URL_FIELD_NAME,
    IServerRollupResponse,
    isPivotEnabled,
    readDoorMetrics,
    readDoorTotals,
    readGroupedRows,
    readRows,
    ROLLUP_DOOR_METRICS_SIZE_PAGE,
    ROLLUP_PIVOT_SIZE_PAGE,
    ROLLUP_SIZE_PAGE,
    SEARCH_AFTER_TOKEN_KEY,
    sortAllocationDimensionsGroupedRows,
    updateTotalCalculationsInRows,
} from "helpers/server-rollup";
import { delimitedFieldHasSingleValue } from "helpers/sizing";
import { columnTreeAsDisplayPreferences as getResultingDoorsLayout, doorsOnlyLayout } from "helpers/tree-traversal";
import { fromJS, List, Map, OrderedSet, Set } from "immutable";
import { fetchJSON } from "lib/fetch";
import { formatMessage } from "lib/globalize-util";
import { isValidNumber, parseNumber } from "lib/number";
import * as SentryUtil from "lib/sentry-util";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import omit from "lodash/omit";
import set from "lodash/set";
import CircularProgress from "material-ui/CircularProgress";
import memoizeOne from "memoize-one";
import me from "models/me";
import * as React from "react";
import shallowCompare from "react-addons-shallow-compare";
import ReactDOMServer from "react-dom/server";
import { IDoorTotalRow, IDoorTotals, IPackInfo } from "reducers/collection";
import "styles/ag-grid/sizingGrid.styl";
import {
    IAttributeFilters,
    IDimensionFilter,
} from "../../product-collection-toolbar/product-collection-toolbar-filters/types";
import { DEFAULT_VALUES_PINNED_ROW } from "./detail-list/helpers";
import { TextEditor } from "./grid-editors";
import { DatePickerCellEditor } from "./grid-editors/date-picker-cell-editor";
import { DropdownSizesEditor } from "./grid-editors/dropdown-sizes-editor";
import { NuRichSelectCellEditor } from "./grid-editors/nu-rich-select-cell-editor";
import { NumberCellEditor } from "./grid-editors/number-cell-editor";
import { SingleChoiceCellEditor } from "./grid-editors/single-choice-cell-editor";
import { RenameableGridHeader } from "./grid-headers/renameable-grid-header";
import { DateCellRenderer } from "./grid-renderers/date-cell-renderer";
import { DefaultRendererVanilla } from "./grid-renderers/default-renderer-vanilla";
import { DefaultRowToggleRenderer } from "./grid-renderers/default-row-toggle-renderer";
import { DimensionValueRendererVanilla } from "./grid-renderers/dimension-value-renderer-vanilla";
import Filter from "./grid-renderers/filter";
import { focusDecorator } from "./grid-renderers/focus-decorator";
import { focusDecoratorVanilla } from "./grid-renderers/focus-decorator-vanilla";
import { FullWidthCellRenderer, IProductCollectionTileGridContext } from "./grid-renderers/full-width-cell-renderer";
import { GroupRowInnerRenderer } from "./grid-renderers/group-row-inner-renderer";
import { ImageCellContainerRenderer } from "./grid-renderers/image-cell-renderer";
import { InventoryUnitsRenderer } from "./grid-renderers/inventory-units-renderer";
import { LinkRenderer } from "./grid-renderers/link-renderer";
import { HeaderCompWithMenu } from "./grid-renderers/menu-group-header-comp";
import { noteDecorator } from "./grid-renderers/note-decorator";
import { HeaderCompWithNote } from "./grid-renderers/notes-header-component";
import { PinnedRowRenderer as PinnedRowRendererVanilla } from "./grid-renderers/pinned-row-renderers/vanilla/pinned-row-renderer";
import { RatingsRenderer } from "./grid-renderers/ratings-renderer";
import { AG_GRID_AUTOCOLUM } from "./grid-renderers/render-utils";
import { SuggestionRenderer } from "./grid-renderers/suggestion-renderer";
import productCollectionGridViewStyles from "./product-collection-grid-view-styles";
import {
    getDoorMetricsSelected,
    hasClientDoorMetrics,
    hasServerDoorMetrics,
    SERVER_DOOR_METRICS,
} from "./tool-panels/dimensions-tool-panel/tabs/metrics/metrics-helpers";
import { KEYBOARD_KEYS } from "constants/keyboard";
import {
    getMetricValueGetterFn,
    getRequiredMetricParams,
} from "helpers/metrics/sibling-value-getter/grid-view-reference-helpers";
import { getReferenceColId } from "helpers/metrics/sibling-value-getter";

const TILE_GROUP_HEIGHT = 72;
const TILE_GUTTER = 8;
export const ROLLUP_INDEX_NOT_FOUND_MESSAGE = "rollup index is not found";

export interface IProps {
    shopViewFields?: IDisplayFields;
    allocationDimensions: IAllocationDimension[];
    allocationDimensionGroups: IAllocationDimension[];
    aggFunc: any;
    allowedValueFields: any;
    brandEntities: any;
    budgets: any;
    bulkAddingItems: boolean;
    bulkItems: any;
    collection?: Map<string, any>;
    capabilities: nuorder.folder.v1.Capabilities;
    collectionBudgetActuals: any;
    collectionId?: string; // indicates if this is used in assortments context or legacy cart.
    copiedAllocation: Map<string, any> | null;
    copiedAllocationName: string;
    copiedAllocationSizes: string;
    copiedDoorAllocationName?: string;
    copyAllocation: (
        copiedAllocation: Map<string, any> | null,
        copiedAllocationName: string,
        copiedAllocationSizes: string,
        copiedBulks?: Map<string, any> | null
    ) => void;
    copyItemsString: string;
    currentViewName: string;
    currentAllocationDimensionView: IPreference[];
    customFilteredItems?: Set<string>;
    // doors collection related
    dates: IAllocationDimensionValue[];
    doors: IDoor[];
    doorClassificationSchema: ISchemaKey[];
    dimensionsPanelApplied: DimensionsPanelReduxState;
    dataEntryType?: UnitsEntryType;
    editable: boolean;
    filter: any;
    filterAssortmentByBrand?: string;
    filterAssortmentBySMU?: string;
    focusOnCellMap: Map<string, any>;
    getSuggestionData: () => ISuggestionObject;
    gridParams: IGridParamsQueryParam;
    hashtags: Set<string>;
    isDraggable: boolean;
    isResetColumns: boolean;
    isResizing?: boolean;
    itemDisplayProperty?: List<any>;
    items?: List<Map<string, any>>;
    itemsId?: string;
    location: any;
    onAddItem: (params: IAddItem) => void;
    onBulkItemsSet?: (ids: OrderedSet<string>) => void;
    onChangeCurrentView: (currentView: List<Map<string, any>>) => void;
    onColumnGroupOpened: (groupId: string, isExpanded: boolean) => void;
    onFilterAssortmentBySMU: (value: SMU_FILTER) => void;
    onFilterChanged?: (gridData: IGridData) => void;
    onFirstDataRendered?: (gridData: IGridData) => void;
    onFocusChange: (collectionId: string, itemsId: string, field: string) => void;
    onFetchCollectionPOI: (poi: string) => void;
    onGridMount?: (api: GridApi, columnApi: ColumnApi, gridRef: React.Ref<AgGridReact>) => void;
    onFocusOnCellMap?: () => Map<string, any>;
    onGridParamsChange: (params: ISaveStateToGridQueryParamsActions, search?: string) => void;
    onViewParamsChange: (
        gridParams: IGridParamsQueryParam,
        updatedDimensionsState: DimensionsPanelReduxState,
        search: string,
        shouldLoadNewSavedView?: boolean
    ) => void;
    onClearViewParams: () => void;
    onLinkProductToItem: (rowId: string) => void;
    onChangeDefaultItemVisibility?: (e?: MouseEvent) => void;
    onMoveItem: (itemsId: string, index: number, destinationIndex: number, rowId: string) => void;
    onMutateCollectionBudget: (id: string, params: any) => void;
    onMutateReviewPlan: (dimensionPath: string[], totalType: any, value: string | number) => void;
    onMutateMetricsReviewPlan: (dimensionPath: string[], metricsKind: MetricsKind, value: string | number) => void;
    onMutateCollectionItem: (itemsId, index, item, value?, meta?, fieldName?) => void;
    onMutateDefaultItem: (item: Map<string, any>, value?: any) => void;
    onClearAllocations: (itemId: string) => void;
    onOpenDeleteConfirmation: (itemsid, itemIndex) => void;
    onOpenProductModal: (id: string, assortmentId?: string, nodeRowIndex?: number) => void;
    onPasteItems: () => void;
    onResetColumns: (reset) => void;
    onRowDataSet: () => void;
    onPasteDoorAllocations: (itemsId: string, door: string, filterAssortmentByBrand: string) => void;
    onSearchChange: (searchText) => void;
    onSetTileViewData: (filteredGridData: IGridData, allGridData: IGridData) => void;
    onSnackbarAdd: (params) => void;
    onSubscribeToItemsUpdates: () => void;
    onToolPanelVisibleChanged: ({ api }: { api: GridApi }) => void;
    onUnsubscribeToItemsUpdates: () => void;
    onUpdateCollectionAttribute: (id, prop, value) => void;
    handleOnChangeDoorGroupingLabel: (event: React.MouseEvent, doorGrouping: string) => void;
    pasteAllocation: (rowData: any, rowIndex: number) => void;
    pivot: any;
    router: any;
    rowGroup: any;
    scrollToIndex?: number;
    searchText: string;
    setCopyItemsString: (newValue: string) => void;
    setSuggestionData: (data: ISuggestionItem[], storeKey: string) => void;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    showTotals: boolean;
    sort: any;
    view: View;
    allocationEnabled: boolean;
    sizingAllocationEnabled: boolean;
    sizePackEnabled: boolean;
    detailFields: IDisplayFields;
    toolpanelOpened: ToolPanels | null;
    sizeRanges: ISizeRange[];
    onResetSizeRanges: () => void;
    onLookupAssortmentErrors: (assortmentsData?: Array<{ [key: string]: string }>) => void;
    dimensionPlanMetrics: string[];
    accessPlan: boolean;
    isRollupMode?: boolean;
    rollupType?: RollupType;
    onExportToExcel: () => void;
    syncingReviewPlans: boolean;
    reviewPlanRows: object[];
    reviewPlan: any;
    onFetchPinnedRows: (showTotals: boolean, dimensionPlanMetrics: string[], accessPlan: boolean) => void;
    isAllocationDimensionModalOpen: boolean;
    recreateServerRollupSnapshot?: boolean;
    doorTotals: IBuiltDoorTotals | {};
    doorTotalRowFiltered?: IDoorTotalRow;
    merchantId: string;
    legacyMerchantId: string;
    sizeTableType?: string;
    merchantPurchaseOrderDoorGroups: IPurchaseOrderDoorGroup[];
    allowDuplicateProduct: boolean;
    merchantCurrency?: ICurrency;
    onPinnedRowOrder: (columnOrder: any) => void;
    pinnedRowColumnOrder: any;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    defaultDateFormat?: string;
    defaultItem?: any;
    showDefaultItem: boolean;
    canShopBrand?: boolean;
    onSetDoorTotals: (doorTotals: IDoorTotals) => void;
    onSetFilteredDoorTotals: (doorTotals: IDoorTotals) => void;
    onSetDoorTotalRow?: (doorTotals: IDoorTotalRow) => void;
    tileGridContext?: IProductCollectionTileGridContext;
    onSetImageCellDimensions: (dimensions: IImageCellDimensions) => void;
    imageCellDimensions: IImageCellDimensions;
    activeValidationMode?: ValidationModes;
    itemsToBeValidated?: Set<any>;
    errorLog?: Map<string, any>;
    isServerRollup?: boolean;
    expandAllGroups: boolean;
    flags?: FeatureFlags;
    onEditDoorGroupingRequest: () => void;
    onImportDoorAttributes: () => void;
    onMutateBagValues: (newBagValue: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    inventoryEnabled?: boolean;
    shipmentReviewDetailRenderer?: (params: ICellRendererParams) => Partial<IDetailCellRendererParams>;
    scrollToDoorsColumnOnLoad: boolean;
    setScrollToDoorsColumnOnLoad: (newValue: boolean) => void;
    startingMutationCount?: number;
    onGridReady?: (e: GridReadyEvent) => void;
    itemsRevision?: number;
    fetchSavedViewData?: (viewData: string[]) => void;
    showTooManyOperationsDialog: () => void;
    onStartCreateRollupSnapshot?: () => void;
    onFetchCollectionDriveItem?: (merchantId: string, rollupId: string, collectionIds: string[]) => void;
    collectionDriveItem?: nuorder.folder.v1.Item;
    merchantAllocationDimensions?: IAllocationDimension[];
    getAlignedGrids?: () => AgGridReact[] | undefined;
    switchingTile?: boolean;
    handleSwitchingTileAndGrid?: (switching: boolean) => void;
    metricsEnabled?: boolean;
    distributionTargets: DistributionTargets;
    isCollapseModeOn: boolean;
    selectedTargetIds: Set<string>;
    collapseMetric?: MetricsKind;
}

export interface IGridContext {
    dimensionFilters: string[][];
    doorIdsWithinClasifications: string[];
    doorTotals: IBuiltDoorTotals | {};
    doors: IDoor[];
    doorTotalRowFiltered: IDoorTotalRow;
    hasDeliveries: boolean;
    poiToItemsToSizeBulksMap?: Map<string, Map<string, number>>;
    onFetchCollectionPOI: (poi: string) => void;
    sizePacks: { [key: string]: { [key1: string]: IPackInfo } };
    onUpdateNote: (itemId: string, colId: string, value: string, node) => void;
    getNoteValue: (itemId: string, colId: string, collectionId?: string) => void;
    getRequestedItemId: () => string;
    getRequestedColumnId: () => string;
    clearRequestedItemId: () => void;
    editable: boolean;
    isServerRollup?: boolean;
    tileGridContext?: IProductCollectionTileGridContext;
    activeValidationMode?: ValidationModes;
    itemsToBeValidated?: Set<any>;
    exportStatuses: Map<string, Map<string, string>>;
    onChangeErrorLogLocation?: (params: any) => void;
    dataEntryType?: UnitsEntryType;
    onMutateBagValues: (newBagValue: IMutateAllocationBagValue) => void;
    allocationBagRef?: {
        allocationBag: AllocationBag;
    };
    getFocusOnCellMap?: () => Map<string, any>;
    distributionTargets: DistributionTargets;
    isDistributionEnabled: boolean;
}
export interface IClipboard {
    groupId?: string;
    doorId: string;
    action: CopyCutPasteActions;
}

export interface ISimpleGridState {
    columnDefs: (ColGroupDef | ColDef)[];
    searchTokens: any[];
    filter: any;
    dragging: any;
    resizing: any[];
    detailList: any[];
    selectedNode: any;
    imageModalOpen?: number; // number associated with row index that should be open
    deleteImageModalOpen?: number; // number associated with row index that should be open
    uploading: boolean;
    selectedImageUrl?: string;
    suppressDragLeaveHidesColumns?: boolean;
    sideBar: SideBarDef;
    // detailCellRenderer: any;
    // detailCellRendererParams: any;
    gridContext: IGridContext;
    isScrollVisible: boolean;
    sidebarHeight?: number;
    isRowDraggedOutside?: boolean;
    // Flag to determine whether to reset pinned rows instead of refresh.
    // This is used when user toggle totals and plan rows
    resetPinnedRows: boolean;
    pinnedRowClickState?: { index: number; columnOrderType: ColumnOrderTypes };
    requestedItemId: string;
    requestedColumnId: string;

    searchAfterTokens: { [key: string]: string[] };
    // tile view
    cardsPerRow?: number;
    startingXPosition?: number;
    allocationClipboardState?: IAllocationClipboardState;
    clipboard?: IClipboard;
    previousExpandedRowsGroupKeyPath: string[][];
    lastToolpanelOpened: string;
    changedViewWhileOnRollup?: boolean;
}

/**
 * Returns an object containing the value type and the current value
 */
const getTypeAndValue = (x) => {
    const valueType = typeof x;
    let t = "string_value";
    let v;
    switch (valueType) {
        case "boolean":
            t = "bool_value";
            v = x;
            break;
        case "string":
            t = "string_value";
            v = (x || "").toString();
            break;
        case "number":
            // NOTE: Number needs to transform to string
            // or will have issue: json: cannot unmarshal number into Go value of type string
            t = "string_value";
            v = x.toString();
            break;
    }
    return { t, v };
};

const getDateTypeAndValue = (x: IDate | undefined) => {
    const t = "date_value";
    let v = x;
    const valueType = typeof x;
    // handle fill operations, since it does not trigger the calendar change
    // value comes in the mm/dd/yyyy format
    if (isDateString(v)) {
        const dateObj = mapValuesToDateObj(v);
        return { v: dateObj, t };
    }
    if (valueType !== "object") {
        v = undefined;
    }
    return { t, v };
};

const getTypeAndValueBasedOnType = (type: string | string[], rowValue?: IItemFieldValue, getterValue?: any) => {
    if (type === ColumnTypes.DateColumn) {
        return getDateTypeAndValue(rowValue?.date_value);
    }
    if (rowValue?.hasOwnProperty(ColDefType.BooleanValue)) {
        return getTypeAndValue(!!rowValue?.bool_value);
    }

    return getTypeAndValue(getterValue);
};

let rowGroupHashtagMap = Map<string, string>();
/**
 * Converts double array of ids to ag-grid compliant filters
 * This is so that save views works and gets saved on the back-end
 * @param dimensionFilters double-array with list of filtered dimension id
 */
export const dimensionFiltersToAgGridFilters = (dimensionFilters: IDimensionFilter[]) => {
    const hadDimensionFilters = dimensionFilters.some((dimension) => dimension.length !== 0);

    if (!hadDimensionFilters) {
        return {};
    }

    // use store filter in special column
    // save filter if date/door populated
    return {
        [GridParamType.DimensionFilters]: {
            filter: "0",
            filterTo: JSON.stringify(dimensionFilters),
            type: "greaterThan",
            filterType: FilterTypes.Text,
        },
    };
};

/**
 * Converts list of objects to ag-grid compliant filters
 * This is so that save views works and gets saved on the back-end
 * @param attributeFilters list of objects
 */
export const attributeFiltersToAgGridFilters = (attributeFilters: IAttributeFilters) => ({
    // save filter if attribute is selected
    ...(Object.keys(attributeFilters).length > 0 && {
        [GridParamType.CustomAttributeFilter]: {
            filter: "0",
            filterTo: JSON.stringify(attributeFilters),
            type: "greaterThan",
            filterType: FilterTypes.Text,
        },
    }),
});

export const defaultValueSetter = ({ colDef, newValue, data }) => {
    const typeAndValue = getTypeAndValue(newValue);
    set(data, ["fields", colDef.field, typeAndValue.t], typeAndValue.v);
    return true;
};

export const dateValueSetter = ({ colDef, newValue, data }) => {
    set(data, ["fields", colDef.field, "date_value"], newValue);
    return true;
};

export const checkType = (value) => {
    return typeof value === "boolean" ? "bool_value" : "string_value";
};

export class ProductCollectionGridView extends React.Component<IProps, ISimpleGridState> {
    static contextTypes = {
        me: PropTypes.object.isRequired,
    };
    gridApi: GridApi;
    private mainGridRef = React.createRef<AgGridReact>();
    private isMobileiOS = /iPad|iPhone/.test(window.navigator.userAgent);
    private columnApi: ColumnApi;
    private dragIndex?: number;
    private itemCurrent?: any;
    private draggingToIndex?: number;
    private draggingItems?: List<any>;
    private expandedRowGroups: string[] = [];
    private requestToExpandNestedGroups: boolean = false;
    private defaultColDef: ColDef = {
        keyCreator(params) {
            const hashtagRegexp = new RegExp(regexes.validHashtag);
            if (hashtagRegexp.test(params.value)) {
                // This is a hashtag.
                const lowerCaseValue = params.value.toLowerCase();
                if (rowGroupHashtagMap.has(lowerCaseValue)) {
                    return rowGroupHashtagMap.get(lowerCaseValue);
                } else {
                    rowGroupHashtagMap = rowGroupHashtagMap.set(lowerCaseValue, params.value);
                }
            } else {
                return params.value?.toUpperCase?.() ?? params.value;
            }
            return params.value;
        },
        suppressKeyboardEvent: suppressDeleteBackspaceKeyboard,
        sortable: true,
        resizable: true,
        editable: () => this.props.editable,
        cellRendererParams: {
            onSearchChange: (e) => {
                e.preventDefault();
                this.props.onSearchChange(e.currentTarget.getAttribute("data-search-text"));
            },
            getPlanByDoor: (door: string) => {
                return this.handleGetPlanByDoor(door);
            },
            isServerSide: this.props.isServerRollup,
        },
        cellEditorParams: {
            getHashtags: () => {
                return this.props.hashtags;
            },
            getSchema: () => {
                return this.props.itemDisplayProperty;
            },
            getPlanByDoor: (door: string) => {
                return this.handleGetPlanByDoor(door);
            },
        },
        // Need this or any blank object cant be set since its nested and ag-grid only sets the parent
        valueSetter: defaultValueSetter,
        cellStyle: ({ api, colDef, node, columnApi }) => {
            const focusOn = this.props.focusOnCellMap.get(`${node.id}.${colDef.colId}`);
            if (focusOn) {
                const myFocused = api.getFocusedCell();
                const myFocusedRowIndex = myFocused && api.getRenderedNodes()[myFocused.rowIndex];
                if (!myFocused || myFocused.column.getColId() !== colDef.colId) {
                    return {
                        border: `1px solid ${focusOn.get("color")}`,
                    };
                }
            }
            return { border: "" };
        },
        floatingFilter: true,
    };
    private initialFiltersLoaded = false;
    private thereAreInitialFilters = false;
    private columnTypes = {
        [ColumnTypes.AllocationDimensionTotal]: {},
        [ColumnTypes.ClassificationTotal]: {},
        [ColumnTypes.DoorBulkQuantity]: {},
        [ColumnTypes.AllocationDimension]: {},
        [ColumnTypes.AllDimensionTotal]: {},
        [ColumnTypes.NestedDimensionTotal]: {},
        [ColumnTypes.DateColumn]: {},
        [ColumnTypes.Metrics]: {
            width: 90,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
            suppressMovable: true,
        },
    };

    private icons = {
        sortPanel: '<span class="ag-icon ag-icon-none"/>',
        dataViewsPanel: '<span class="ag-icon ag-icon-eye"/>',
        shopPanel: "<span><b>+</b></span>",
        dimensionsPanel: ReactDOMServer.renderToString(<StoreMallDirectoryOutlinedIcon />),
    };

    private autoGroupColumnDef = {
        comparator: (valueA: any, valueB: any, nodeA: RowNode, nodeB: RowNode) => {
            const { allowedValueFields, gridParams, itemDisplayProperty, allocationDimensions } = this.props;
            const rowGroup = gridParams[GridParamType.RowGroup];
            const fieldName = rowGroup?.[0];
            if (fieldName.includes(ALLOCATION_DIMENSION_ID_PREFIX)) {
                return getSortOrderForAutoGroupColumn({
                    allowedValueFields,
                    gridParams,
                    itemDisplayProperty,
                    allocationDimensions,
                    valueA,
                    valueB,
                    nodeA,
                    nodeB,
                });
            }
            const displayProperty = itemDisplayProperty.find((field) => field && field.get("field_name") === fieldName);

            if (displayProperty) {
                const schemaKey = displayProperty.toJS();
                const comparatorFunc = getComparator(schemaKey, { allowedValueFields });
                return comparatorFunc.bind(this)(valueA, valueB, nodeA, nodeB);
            }
            const res = valueA == valueB ? 0 : valueA > valueB ? 1 : -1;
            return res;
        },
        cellRendererParams: {
            isServerSide: this.props.isServerRollup,
        },
    };

    private statusBar = {
        statusPanels: [{ statusPanel: "agAggregationComponent" }],
    };

    protected get isGridTileView() {
        return this.props.isServerRollup && this.props.view === View.Tile;
    }

    private tossedErrorForIndex = false;
    private visibleColumnsSpan;
    private excelStyles: ExcelStyle[] = [
        {
            id: EXCEL_STYLES.STRING_CELL_CLASS,
            dataType: "String",
        },
        {
            id: EXCEL_STYLES.CURRENCY_CELL_CLASS,
            numberFormat: { format: "#,##0.00" },
        },
        {
            id: EXCEL_STYLES.PERCENT_CELL_CLASS,
            numberFormat: { format: "0%" },
        },
        {
            id: EXCEL_STYLES.INTEGER_CELL_CLASS,
            numberFormat: { format: "0" },
        },
    ];

    private muiStyles = {
        spinner: {
            display: "block",
            margin: "60px auto 0px auto",
        },
    };

    private gridComponents = {
        actionsRenderer: () => "",
        defaultRenderer: focusDecoratorVanilla(DefaultRendererVanilla),
        dimensionValueRenderer: DimensionValueRendererVanilla,
        customPinnedRowRenderer: PinnedRowRendererVanilla,
        defaultRowToggleRenderer: DefaultRowToggleRenderer,
        headerCompWithNote: HeaderCompWithNote,
        headerCompWithMenu: HeaderCompWithMenu,

        // React renderers
        // Cell Renderer
        linkRenderer: noteDecorator(focusDecorator(LinkRenderer)),
        ratingRenderer: noteDecorator(focusDecorator(RatingsRenderer)),
        suggestionRenderer: noteDecorator(focusDecorator(SuggestionRenderer)),
        imageCellRenderer: noteDecorator(ImageCellContainerRenderer),
        dateCellRenderer: noteDecorator(DateCellRenderer),
        inventoryUnitsRenderer: noteDecorator(InventoryUnitsRenderer),
        [Editors.GenericReduxConnectedRenderer]: noteDecorator(GenericReduxConnectedRenderer),
        // Header component
        renameableHeaderComponent: RenameableGridHeader,

        // Tool Panel Component
        smuToolPanel: SMUToolPanel,
        dataViewPanel: DataViewPanel,
        sortToolPanel: SortToolPanel,
        shopToolPanel: ShopToolPanel,
        filterColumn: Filter,
        dimensionsToolPanel: DimensionsToolPanel,

        // Cell Editor
        [Editors.NumberCellEditor]: NumberCellEditor,
        textEditor: TextEditor,
        [Editors.DropdownSizesEditor]: DropdownSizesEditor,
        [Editors.NuRichSelectCellEditor]: NuRichSelectCellEditor,
        [Editors.DatePickerCellEditor]: DatePickerCellEditor,
        [Editors.SingleChoiceCellEditor]: SingleChoiceCellEditor,
        [Editors.MultipleChoiceCellEditor]: MultipleChoiceCellEditorWrapper,
        [Editors.PackTypeCellEditor]: PackTypeCellEditorEditorWrapper,
        fullWidthCellRenderer: FullWidthCellRenderer,
        groupRowInnerRenderer: GroupRowInnerRenderer,
    };

    private _handleSaveGridLayout = (columnSchemaKeys, allocationDimensionsColumns?) => {
        const { currentAllocationDimensionView, onChangeCurrentView } = this.props;

        const newDoors: IPreference = {
            field_name: ARTIFICAL_ALLOCATION_DIMENSIONS_NODE,
            display: { hidden: false },
            grid_column: {},
        };

        newDoors.children = mergeAllocationDimensions(currentAllocationDimensionView, allocationDimensionsColumns);
        onChangeCurrentView(columnSchemaKeys.push(...(newDoors.children ? [Map<string, any>(newDoors)] : [])));
    };

    private handleSaveGridLayout = debounce(this._handleSaveGridLayout, 600);

    private handleColumnRowGroupChanged = debounce((params) => {
        const { columns, source } = params;
        if (COLUMN_EVENT_SOURCES.includes(source)) {
            const rowGroup = columns.map((column) => column.colId);
            this.props.onGridParamsChange({
                type: SaveStateToGridQueryParamsType.SetRowGroup,
                value: rowGroup,
            });
        }
    }, 600);

    private handleColumnValueChanged = debounce((params) => {
        const { columnApi, source } = params;
        if (COLUMN_EVENT_SOURCES.includes(source)) {
            const columns = (columnApi as any).getValueColumns();
            const aggFunc = columns.map((column) => {
                return {
                    column: column.colId,
                    aggFunc: column.aggFunc,
                };
            });
            this.props.onGridParamsChange({
                type: SaveStateToGridQueryParamsType.SetAggFunction,
                value: aggFunc,
            });
        }
    }, 600);

    private focusField;

    private handleColumnPivotChanged = debounce((params) => {
        const { columns, columnApi, source } = params;
        if (COLUMN_EVENT_SOURCES.includes(source)) {
            const pivotColumns = columns.map((column) => column.colId);
            const mode = (columnApi as any).isPivotMode();
            let pivotParams = { mode, columns: pivotColumns };
            if (!pivotParams.mode && (!pivotParams.columns || pivotParams.columns.length === 0)) {
                pivotParams = undefined;
            }

            this.props.onGridParamsChange([
                {
                    type: SaveStateToGridQueryParamsType.SetPivot,
                    value: pivotParams,
                },
                ...(this.props.isServerRollup
                    ? [
                          {
                              type: SaveStateToGridQueryParamsType.SetSort,
                              value: undefined,
                          },
                      ]
                    : []),
            ]);
        }
    }, 600);

    private debouncedSubscribeToItemUpdates = debounce(() => {
        this.props.onSubscribeToItemsUpdates();
    }, 3000);

    private debouncedUpdateTileViewAfterResizeWindow = debounce(
        this.refreshTileViewAndPersistGroupsState.bind(this),
        600
    );

    private debouncedFilterModel?: IFiltersQueryParam | null;

    private applyDebouncedFilterModel = debounce(() => {
        if (!this.isGridReady()) {
            this.applyDebouncedFilterModel();
        } else {
            // `undefined` is valid value for `this.debouncedFilterModel` when we need to clear any applied filters.
            this.gridApi.setFilterModel(this.debouncedFilterModel);
            this.debouncedFilterModel = undefined;
        }
    }, 30);

    private debouncedSetFilterModel = (filterModel?: IFiltersQueryParam) => {
        this.debouncedFilterModel = filterModel ?? get(this.props.gridParams, GridParamType.Filter);
        this.applyDebouncedFilterModel();
    };

    private getFilterModel() {
        const filterModel =
            this.debouncedFilterModel ?? (this.isGridReady() ? this.gridApi.getFilterModel() : undefined);
        return isEmpty(filterModel) ? null : filterModel;
    }

    private getRowData = (items, allocationBag?: AllocationBag) => {
        if (!items) {
            return null;
        }

        const dimensionFilters = getDimensionFilters(this.props.gridParams);
        const filterDimensionValues = {};

        this.props.allocationDimensions.forEach((allocationDimension, index) => {
            if (dimensionFilters[index] && dimensionFilters[index].length > 0) {
                filterDimensionValues[allocationDimension.id] = dimensionFilters[index];
            }
        });

        // To explode on-demand, pass in only allocation dimensions being used for grouping
        if (this.props.allocationDimensionGroups.length > 0) {
            // Reset counter cache every time we will explode rows so we will get consistent row id when calling getRowId
            const explodedItems = items.toJS().reduce((acc, item) => {
                return acc.concat(
                    !allocationBag
                        ? explodeRowByAllocationDimensionsLegacy(
                              item,
                              ItemBagType.Bulks,
                              this.props.allocationDimensions,
                              this.props.allocationDimensionGroups,
                              filterDimensionValues
                          )
                        : explodeRowByAllocationDimensions(
                              item,
                              allocationBag,
                              ItemBagType.Bulks,
                              this.props.allocationDimensions,
                              this.props.allocationDimensionGroups,
                              filterDimensionValues
                          )
                );
            }, []);

            return explodedItems;
        }

        return items.toJS();
    };

    private setGridRowData = (items: List<any>) => {
        if (this.gridApi && !this.props.isServerRollup) {
            this.gridApi.setRowData(this.getRowData(items, this.props.allocationBagRef?.allocationBag));
        }
    };

    /**
     * Recompute aggregated columns when there is row group and there is aggregated values.
     * This is particularly needed when we have units columns.
     */
    private maybeRecomputeAggregatedColumns = debounce(() => {
        if (this.props.rowGroup?.length > 0 && this.props.gridParams?.[GridParamType.AggFunc]?.length > 0) {
            this.gridApi?.refreshClientSideRowModel("aggregate");
        }
    }, 500);

    private generateDefaultColumnDefsProps = debounce((props: IProps, otherStateProps?: any) => {
        const columnDefs = generateDefaultColumnDefs({
            ...props,
            isServerSide: this.props.isServerRollup,
            isGridTileView: this.isGridTileView,
            dimensionToolPanelEnabled: this.props.flags?.dimensionsToolPanel,
            flags: this.props.flags,
        });

        if (props.isServerRollup) {
            // We need this to mimic loading overlay in SR
            this.gridApi.showNoRowsOverlay();
        } else if (this.props.items?.size > 0) {
            this.gridApi?.showLoadingOverlay();
        }

        this.setState({
            columnDefs,
            ...(otherStateProps || {}),
        });

        process.nextTick(() => {
            SentryUtil.finishSpan(AssortmentTransactions.SwitchView, AssortmentEvents.SwitchView);
            SentryUtil.finishTransaction(AssortmentTransactions.SwitchView);
            // We can leave view switching here, as view switching will always rerender columns
            this.handleResetRowHeight();
            this.gridApi.refreshCells({ columns: [IMAGE_URL] });
        });
    }, 250);

    private refreshPinnedRows = debounce(() => {
        if (this.gridApi) {
            const pinnedRows = [];
            for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
                pinnedRows.push(this.gridApi.getPinnedTopRow(i));
            }
            // This needs to be called so value getter will be re-evaluated
            if (pinnedRows.length > 0) {
                this.gridApi.expireValueCache();
                this.gridApi.refreshCells({
                    rowNodes: pinnedRows,
                });
            }
        }
    }, 500);

    private resetPinnedRows = debounce((totalRowResponse?: ITotalRowResponse) => {
        if (this.gridApi) {
            this.setState({
                resetPinnedRows: false,
            });
            const pinnedRowSorts = get(this.props.gridParams, GridParamType.PinnedRowSort);
            const shouldDisplayPinnedRows = !this.isGridTileView;
            let pinnedRows;
            const assortmentDimensionMetrics = this.props.metricsEnabled;
            if (assortmentDimensionMetrics && shouldDisplayPinnedRows && this.props.showTotals) {
                if (this.props.isServerRollup && totalRowResponse !== undefined) {
                    pinnedRows = getServerRollupAssortmentTotalsRow(totalRowResponse);
                } else {
                    pinnedRows = getAssortmentTotalsRow();
                }
            } else if (assortmentDimensionMetrics && this.props.dimensionsPanelApplied.metrics.showAssortmentPlan) {
                const reviewPlanObj = this.props.reviewPlan.toJS();
                pinnedRows = Object.values(PlanMetricRows).map((planType) => {
                    return {
                        plan: {},
                        planType,
                        reviewPlan: reviewPlanObj,
                    };
                });
            } else {
                pinnedRows = validatePinnedRows(
                    shouldDisplayPinnedRows && this.props.showTotals,
                    shouldDisplayPinnedRows && this.props.accessPlan,
                    this.props.reviewPlanRows,
                    pinnedRowSorts,
                    (index) => {
                        (
                            get(this.props.gridParams, GridParamType.PinnedRowSort) || [
                                {
                                    id: generatePinnedRowId(pinnedRows[index].totalType, pinnedRows[index].planType),
                                    order: ColumnOrderTypes.None,
                                },
                            ]
                        ).forEach((pinnedRowSort) => {
                            const arrSort = pinnedRowSort.id.split("-");
                            if (
                                arrSort[0] !== `${pinnedRows[index].totalType}` ||
                                (pinnedRows[index].planType && arrSort[1] !== `${pinnedRows[index].planType}`)
                            ) {
                                pinnedRowSort.order = ColumnOrderTypes.None;
                            }

                            const clickState = {
                                index,
                                columnOrderType:
                                    pinnedRowSort && pinnedRowSort.order !== ColumnOrderTypes.None
                                        ? pinnedRowSort.order === ISortOrder.Ascending
                                            ? ColumnOrderTypes.Asc
                                            : ColumnOrderTypes.Desc
                                        : ColumnOrderTypes.None,
                            };
                            if (clickState.columnOrderType === ColumnOrderTypes.Asc) {
                                clickState.columnOrderType = ColumnOrderTypes.Desc;
                            } else if (clickState.columnOrderType === ColumnOrderTypes.Desc) {
                                clickState.columnOrderType = ColumnOrderTypes.None;
                            } else {
                                clickState.columnOrderType = ColumnOrderTypes.Asc;
                            }

                            const gridParams: { type: SaveStateToGridQueryParamsType; value: any } = {
                                type: SaveStateToGridQueryParamsType.SetPinnedRowSort,
                                value:
                                    clickState.columnOrderType !== ColumnOrderTypes.None
                                        ? [
                                              {
                                                  id: generatePinnedRowId(
                                                      pinnedRows[index].totalType,
                                                      pinnedRows[index].planType
                                                  ),
                                                  order:
                                                      clickState.columnOrderType === ColumnOrderTypes.Asc
                                                          ? ISortOrder.Ascending
                                                          : ISortOrder.Descending,
                                                  sort_by: ISortSortBy.DoorMetrics,
                                              },
                                          ]
                                        : undefined,
                            };
                            this.props.onGridParamsChange(gridParams);
                            this.setState({
                                pinnedRowClickState: clickState,
                            });
                            pinnedRows.forEach((pinnedRow) => {
                                pinnedRow.pinnedRowSorts = gridParams.value;
                            });
                        });
                    }
                );
            }
            if (this.props.defaultItem && this.props.showDefaultItem) {
                pinnedRows.push({
                    ...this.props.defaultItem.toJS(),
                    type: DEFAULT_VALUES_PINNED_ROW,
                });
            }
            this.gridApi.setPinnedTopRowData(pinnedRows);
        }
    }, 250);

    private handlePinnedRowDataChange = () => {
        const pinnedRowSorts = get(this.props.gridParams, GridParamType.PinnedRowSort);
        if (pinnedRowSorts) {
            pinnedRowSorts.forEach((pinnedRowSort) => {
                let index = -1;
                pinnedRowSorts.forEach((pinnedRowSort) => {
                    const arrSort = pinnedRowSort.id.split("-");
                    for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
                        const data = this.gridApi.getPinnedTopRow(i).data;
                        if (data.totalType === arrSort[0] && (arrSort.length === 1 || data.planType === arrSort[1])) {
                            index = i;
                            break;
                        }
                    }
                });
                const columnOrderType =
                    pinnedRowSort.order === ISortOrder.Ascending ? ColumnOrderTypes.Asc : ColumnOrderTypes.Desc;
                this.setState({
                    pinnedRowClickState: {
                        index,
                        columnOrderType,
                    },
                });
                this.props.onPinnedRowOrder({
                    columnOrderType,
                    gridContext: this.getGridContext(this.props),
                    row: this.gridApi.getPinnedTopRow(index),
                });
            });
        } else {
            if (this.state.pinnedRowClickState) {
                this.props.onPinnedRowOrder({
                    columnOrderType: ColumnOrderTypes.None,
                    gridContext: this.getGridContext(this.props),
                    row: this.gridApi.getPinnedTopRow(this.state.pinnedRowClickState.index),
                });
                if (
                    this.gridApi.getPinnedTopRowCount() &&
                    this.gridApi.getPinnedTopRow(this.state.pinnedRowClickState.index)
                ) {
                    this.gridApi.getPinnedTopRow(this.state.pinnedRowClickState.index).data.pinnedRowSorts = [];
                }
            }
        }
        for (let i = 0; i < this.gridApi.getPinnedTopRowCount(); i++) {
            const data = this.gridApi.getPinnedTopRow(i).data;
            if (data) {
                data.pinnedRowSorts = pinnedRowSorts;
            }
        }
    };

    constructor(props: any) {
        super(props);

        this.state = {
            columnDefs: props.itemDisplayProperty
                ? generateDefaultColumnDefs({
                      ...props,
                      isServerSide: props.isServerRollup,
                      isGridTileView: props.isServerRollup && props.view === View.Tile,
                  })
                : [],
            searchTokens: [],
            searchAfterTokens: {},
            filter: {},
            dragging: {},
            resizing: [],
            selectedNode: {},
            imageModalOpen: undefined,
            deleteImageModalOpen: undefined,
            uploading: false,
            selectedImageUrl: undefined,
            detailList: [],
            sideBar: this.handleSideBarDef(props),
            gridContext: this.getGridContext(props),
            isScrollVisible: false,
            isRowDraggedOutside: undefined,
            resetPinnedRows: true,
            requestedItemId: undefined,
            requestedColumnId: undefined,
            previousExpandedRowsGroupKeyPath: [],
            lastToolpanelOpened: ToolPanels.Columns,
            changedViewWhileOnRollup: false,
        };
    }

    shouldComponentUpdate(nextProps: IProps, nextState) {
        if (this.state.dragging.column || this.state.dragging.columns || this.state.resizing.length) {
            return false;
        }
        if (nextProps.isAllocationDimensionModalOpen) {
            return false;
        }
        const shouldUpdateByProps = shallowEqualAndExclude(nextProps, this.props, ["location"]);
        const shouldUpdateByState = shallowCompare(this, this.props, nextState);

        if (!shouldUpdateByProps && !shouldUpdateByState) {
            const [nextLocation, currentLocation] = [nextProps.location, this.props.location];
            return nextLocation !== currentLocation
                ? !isEqual(
                      { ...nextLocation.query, gridParams: "", [DOOR_GROUP_QUERY_KEY]: "" },
                      { ...currentLocation.query, gridParams: "", [DOOR_GROUP_QUERY_KEY]: "" }
                  )
                : false;
        }

        return true;
    }

    componentWillUnmount() {
        this.props.isServerRollup &&
            window.removeEventListener("resize", this.debouncedUpdateTileViewAfterResizeWindow);
        this.teardownActions.forEach((fn) => fn.call(this));
    }

    debouncedHandleFilterChanged = debounce(() => this.gridApi.onFilterChanged(), 300);

    componentDidUpdate(prevProps: IProps, prevState: ISimpleGridState) {
        const {
            gridContext: { poiToItemsToSizeBulksMap, exportStatuses },
        } = this.state;

        if (prevProps.searchText !== this.props.searchText) {
            this.setState({
                searchTokens: this.tokenizeSearchTerm(this.props.searchText),
            });

            if (this.props.searchText) {
                SentryUtil.startTransaction(AssortmentTransactions.Search);
                SentryUtil.startSpan(AssortmentTransactions.Search, AssortmentEvents.Search);
            }
        }

        if (prevState.changedViewWhileOnRollup) {
            this.setState({ changedViewWhileOnRollup: false });
        }

        const itemDisplayPropertyChanged =
            (this.props.itemDisplayProperty && !prevProps.itemDisplayProperty) ||
            (this.props.isRollupMode && !isEqual(this.props.itemDisplayProperty, prevProps.itemDisplayProperty));

        const prevViewName = prevProps.currentViewName;
        const newViewName = this.props.currentViewName;
        const toolpanelOpened = this.gridApi?.getOpenedToolPanel();
        if (newViewName !== prevViewName) {
            // We will need to reconcile, as DataViewPanel assigns the same schema to a different
            // view called DEFAULT_NAME. Clearly a design flaw.
            // We can regenerate columns for now, as the performance impact will only be on
            // switching to the Default view.
            if (
                this.props.itemDisplayProperty !== prevProps.itemDisplayProperty ||
                this.props.currentAllocationDimensionView !== prevProps.currentAllocationDimensionView
            ) {
                this.generateDefaultColumnDefsProps(this.props, { dragging: {}, resizing: [] });
            }
        } else if (
            (itemDisplayPropertyChanged && !this.gridApi) ||
            !isEqual(prevProps.merchantCurrency, this.props.merchantCurrency)
        ) {
            this.generateDefaultColumnDefsProps(this.props);
        } else if (prevProps.showDefaultItem !== this.props.showDefaultItem) {
            this.generateDefaultColumnDefsProps(this.props); // Got to do it; new showDefaultItem param needs to be passed
            this.resetPinnedRows();
        }

        const gridFilterModel = get(this.props.gridParams, GridParamType.Filter);
        if (
            !isEqual(gridFilterModel, get(prevProps.gridParams, GridParamType.Filter)) ||
            newViewName !== prevViewName
        ) {
            this.debouncedSetFilterModel(gridFilterModel);
        }

        if (this.props.flags?.dimensionsToolPanel !== prevProps.flags?.dimensionsToolPanel) {
            this.setState({ sideBar: this.handleSideBarDef(this.props) });
        }

        if (this.props.canShopBrand !== prevProps.canShopBrand) {
            this.setState({ sideBar: this.handleSideBarDef(this.props) });
        }

        if (this.gridApi) {
            if (this.props.bulkItems.size !== prevProps.bulkItems.size && this.props.bulkItems.size === 0) {
                this.gridApi.deselectAll();
            }

            // when the items in the state have changed, update the grid with the latest rows
            if (this.props.items && !this.props.items.equals(prevProps.items)) {
                this.setGridRowData(this.props.items);

                const { selectedNode, requestedColumnId } = this.state;

                if (selectedNode && requestedColumnId) {
                    this.setState({
                        requestedColumnId: undefined,
                    });
                    process.nextTick(() =>
                        this.gridApi.refreshCells({
                            force: true,
                            rowNodes: [selectedNode],
                            columns: [requestedColumnId],
                        })
                    );
                }
            }

            if (
                this.props.showTotals !== prevProps.showTotals ||
                !isEqual(this.props.dimensionPlanMetrics, prevProps.dimensionPlanMetrics) ||
                this.props.accessPlan !== prevProps.accessPlan
            ) {
                this.setState(
                    {
                        resetPinnedRows: true,
                    },
                    () => {
                        this.props.onFetchPinnedRows(
                            this.props.showTotals,
                            this.props.dimensionPlanMetrics,
                            this.props.accessPlan
                        );
                    }
                );

                if (this.props.showTotals) {
                    this.gridApi.refreshServerSideStore({ purge: false });
                }
            }

            if (this.props.view !== prevProps.view) {
                if (this.props.view === View.Grid) {
                    if (this.props.items) {
                        this.setGridRowData(this.props.items);
                    }
                }

                if (this.props.isServerRollup) {
                    this.setState({ sideBar: this.handleSideBarDef(this.props) });
                    // Re-build columns
                    this.generateDefaultColumnDefsProps(this.props);
                    // This is needed to reset row styles and height
                    this.debouncedHandleFilterChanged();
                    if (prevProps.view && this.props.view) {
                        this.setState({ changedViewWhileOnRollup: true });
                    }
                }

                if (this.props.view === View.History || prevProps.view === View.History) {
                    this.setState({ sideBar: this.handleSideBarDef(this.props) });
                }
            }

            if (prevProps.scrollToIndex !== this.props.scrollToIndex && this.props.scrollToIndex !== undefined) {
                const index = this.props.scrollToIndex;
                setTimeout(() => {
                    if (this.props.items && this.props.items.size > index) {
                        this.gridApi.ensureIndexVisible(index, "bottom");
                    }
                }, 100);
            }

            if (this.props.bulkAddingItems !== prevProps.bulkAddingItems) {
                if (this.props.bulkAddingItems) {
                    this.gridApi.showLoadingOverlay();
                } else {
                    this.gridApi.hideOverlay();
                }
            }

            if (this.props.startingMutationCount !== prevProps.startingMutationCount) {
                if (this.props.startingMutationCount === 0) {
                    this.gridApi.hideOverlay();
                }
            }

            if (!this.props.focusOnCellMap.equals(prevProps.focusOnCellMap)) {
                const rowsToRefresh = this.generateFocusRowsFromFocusMaps(
                    prevProps.focusOnCellMap,
                    this.props.focusOnCellMap
                );

                // Refresh cells that are being focused by other users.
                // we are using AG's private method here. This is the only way we can update the cell style without `redrawRows`
                // or `refreshCells` which will trigger flashing.
                // @ts-ignore ignoring TS warning against using private method
                this.gridApi.rowRenderer.getCellCtrls(rowsToRefresh, undefined).forEach((cellCtrl) => {
                    cellCtrl.refreshCell({
                        suppressFlash: true,
                        forceRefresh: true,
                        newData: false,
                    });
                });
            }

            const isGridRowGroupChanged = !isEqual(
                this.props.gridParams?.[GridParamType.RowGroup],
                prevProps.gridParams?.[GridParamType.RowGroup]
            );
            const isAggValuesChanged = !isEqual(
                this.props.gridParams?.[GridParamType.AggFunc],
                prevProps.gridParams?.[GridParamType.AggFunc]
            );
            // detect dimension params change (Dimension panel sidebar or Show doors menu)
            const isDimensionParamsChanged = this.props.dimensionsPanelApplied !== prevProps.dimensionsPanelApplied;
            const isPivotColsChanged = !isEqual(
                this.props.gridParams?.[GridParamType.Pivot],
                prevProps.gridParams?.[GridParamType.Pivot]
            );

            // detect changes from drop-down filter
            const isDimensionFilterChanged = !isEqual(
                this.props.gridParams?.[GridParamType.Filter]?.[GridParamType.DimensionFilters],
                prevProps.gridParams?.[GridParamType.Filter]?.[GridParamType.DimensionFilters]
            );
            // detect mutation from manage allocation dimension page
            const isHistoryView = this.props.view === View.History;
            const isAllocationDimensionValueChanged = this.props.isServerRollup
                ? !isEqual(this.props.doors, prevProps.doors)
                : this.props.doors !== prevProps.doors;
            const isMerchantPurchaseOrderDoorGroupsChanged =
                this.props.merchantPurchaseOrderDoorGroups !== prevProps.merchantPurchaseOrderDoorGroups;
            const isDoorTotalsChanged = this.props.doorTotals !== prevProps.doorTotals;
            const isPoiToItemsToSizeBulksMapChanged =
                prevProps.poiToItemsToSizeBulksMap !== this.props.poiToItemsToSizeBulksMap;
            const isCustomFilteredItemsChanged = prevProps.customFilteredItems !== this.props.customFilteredItems;
            const isDoorClasificationFilterChanged = !isEqual(
                this.props.gridParams?.[GridParamType.Filter]?.[GridParamType.CustomAttributeFilter],
                prevProps.gridParams?.[GridParamType.Filter]?.[GridParamType.CustomAttributeFilter]
            );
            if (
                prevState.searchTokens !== this.state.searchTokens ||
                prevProps.filterAssortmentByBrand !== this.props.filterAssortmentByBrand ||
                prevProps.filterAssortmentBySMU !== this.props.filterAssortmentBySMU ||
                isDimensionFilterChanged ||
                isCustomFilteredItemsChanged ||
                isDoorClasificationFilterChanged
            ) {
                this.setState({
                    gridContext: this.getGridContext(this.props),
                });
                this.debouncedHandleFilterChanged();
            }
            const wasAllocationDimensionModalOpen =
                !this.props.isAllocationDimensionModalOpen &&
                prevProps.isAllocationDimensionModalOpen !== this.props.isAllocationDimensionModalOpen;
            const shouldResetColumns =
                this.props.isResetColumns && this.props.itemDisplayProperty !== prevProps.itemDisplayProperty;

            let shouldGenerateColumns = false;
            if (this.props.isServerRollup) {
                const prevData = {
                    query: omit(prevProps.gridParams, ["filter", "rowGroup", "aggFunc", "pivot"]),
                    tileSettings: prevProps.tileGridContext?.tileSettings,
                    tileViewSize: prevProps.tileGridContext?.tileViewSize,
                };

                const currentData = {
                    query: omit(this.props.gridParams, ["filter", "rowGroup", "aggFunc", "pivot"]),
                    tileSettings: this.props.tileGridContext?.tileSettings,
                    tileViewSize: this.props.tileGridContext?.tileViewSize,
                };

                shouldGenerateColumns = !isEqual(prevData, currentData);
            }

            const showDoorsChanged =
                this.props.dimensionsPanelApplied.showDoors !== prevProps.dimensionsPanelApplied.showDoors;
            const showTotalsChanged =
                this.props.dimensionsPanelApplied.metrics.showAssortmentTotals !==
                prevProps.dimensionsPanelApplied.metrics.showAssortmentTotals;
            const assortmentDimensionMetrics = this.props.metricsEnabled;
            const isSRDoorGroupingsEnabled =
                this.props.isServerRollup && assortmentDimensionMetrics && this.props.allocationEnabled;

            const isSRDoorMetricsSelectionChanged =
                isSRDoorGroupingsEnabled &&
                !isEqual(
                    getDoorMetricsSelected(prevProps.dimensionsPanelApplied.metrics.values),
                    getDoorMetricsSelected(this.props.dimensionsPanelApplied.metrics.values)
                );

            const isSRDoorAttributesSelectionChanged =
                isSRDoorGroupingsEnabled &&
                !isSRDoorMetricsSelectionChanged &&
                !isEqual(prevProps.dimensionsPanelApplied.attributes, this.props.dimensionsPanelApplied.attributes);

            const isSRDimensionsSelectionChanged =
                isSRDoorGroupingsEnabled &&
                !isSRDoorAttributesSelectionChanged &&
                !isEqual(prevProps.dimensionsPanelApplied.dimensions, this.props.dimensionsPanelApplied.dimensions);

            if (
                (isSRDoorGroupingsEnabled && showDoorsChanged) ||
                isSRDoorMetricsSelectionChanged ||
                isSRDoorAttributesSelectionChanged ||
                isSRDimensionsSelectionChanged ||
                showTotalsChanged
            ) {
                this.debouncedHandleFilterChanged();
            }

            if (
                this.props.editable !== prevProps.editable ||
                isAllocationDimensionValueChanged ||
                isDimensionFilterChanged ||
                isDoorClasificationFilterChanged ||
                isMerchantPurchaseOrderDoorGroupsChanged ||
                wasAllocationDimensionModalOpen ||
                shouldResetColumns ||
                shouldGenerateColumns
            ) {
                this.generateDefaultColumnDefsProps(this.props, {});
                this.props.onResetColumns(isHistoryView);
            } else if (this.props.dimensionsPanelApplied.showDoors) {
                for (const paramName of Object.keys(this.props.dimensionsPanelApplied)) {
                    // We don't want to regenerate col def for these keys
                    if (paramName === "collapsedGroupIds") {
                        continue;
                    }

                    if (this.props.dimensionsPanelApplied[paramName] !== prevProps.dimensionsPanelApplied[paramName]) {
                        // Set flag to jump to Doors columns ONLY when "Show Doors" is first toggled ON
                        if (showDoorsChanged && this.props.dimensionsPanelApplied.showDoors) {
                            this.props.setScrollToDoorsColumnOnLoad(true);
                        }
                        this.generateDefaultColumnDefsProps(this.props, {});
                        break;
                    }
                }
            } else if (showDoorsChanged && !this.props.dimensionsPanelApplied.showDoors) {
                // Specific to hiding doors
                this.generateDefaultColumnDefsProps(this.props, {});
            }

            if (this.props.isCollapseModeOn !== prevProps.isCollapseModeOn) {
                this.generateDefaultColumnDefsProps(this.props, {});
            }

            if (this.props.currentViewName !== prevProps.currentViewName) {
                if (
                    this.props.currentAllocationDimensionView === prevProps.currentAllocationDimensionView &&
                    !itemDisplayPropertyChanged
                ) {
                    // Saving view (thus no change in current item disposition but in view name)
                    SentryUtil.finishSpan(AssortmentTransactions.SaveView, AssortmentEvents.SaveView);
                    SentryUtil.finishTransaction(AssortmentTransactions.SaveView);
                }
            }

            if (
                !isEqual(get(this.props.gridParams, GridParamType.Sort), get(prevProps.gridParams, GridParamType.Sort))
            ) {
                const sort: SortModelItem[] = get(this.props.gridParams, GridParamType.Sort);
                const finalSort = sort ?? removeSortFromColumnStateList(this.columnApi.getColumnState());
                process.nextTick(() => this.columnApi.applyColumnState({ state: finalSort }));
            }

            if (
                !isEqual(
                    get(this.props.gridParams, GridParamType.PinnedRowSort),
                    get(prevProps.gridParams, GridParamType.PinnedRowSort)
                ) ||
                (!this.state.pinnedRowClickState && get(this.props.gridParams, GridParamType.PinnedRowSort)) ||
                isDoorTotalsChanged
            ) {
                this.handlePinnedRowDataChange();
            }

            if (
                isDimensionFilterChanged ||
                isDimensionParamsChanged ||
                isAllocationDimensionValueChanged ||
                isDoorTotalsChanged ||
                (isPoiToItemsToSizeBulksMapChanged && this.props.poiToItemsToSizeBulksMap) ||
                this.props.sizePacks !== prevProps.sizePacks ||
                this.props.itemsToBeValidated !== prevProps.itemsToBeValidated ||
                this.props.allocationBagRef !== prevProps.allocationBagRef ||
                this.props.errorLog !== prevProps.errorLog ||
                this.props.distributionTargets !== prevProps.distributionTargets ||
                this.props.flags.assortmentManageDistribution !== prevProps.flags.assortmentManageDistribution
            ) {
                // context can be updated then.
                this.setState({
                    gridContext: this.getGridContext(this.props),
                });

                // We do not need to refresh cells when totals change.
                if (!isDoorTotalsChanged) {
                    process.nextTick(() => {
                        this.gridApi.refreshCells();
                    });
                }
            }

            if (
                this.props.isServerRollup &&
                !isEqual(this.props.doorTotalRowFiltered, prevProps.doorTotalRowFiltered)
            ) {
                this.setState({
                    gridContext: this.getGridContext(this.props),
                });
            }

            if (this.state.gridContext.allocationBagRef !== prevState.gridContext.allocationBagRef) {
                this.gridApi.expireValueCache();
                this.maybeRecomputeAggregatedColumns();
            }

            if (
                prevState.gridContext.poiToItemsToSizeBulksMap !== poiToItemsToSizeBulksMap ||
                prevState.gridContext.exportStatuses !== exportStatuses
            ) {
                this.gridApi?.expireValueCache();
                this.gridApi?.refreshCells();
            }

            // Pinned rows rely on `doorTotals` in grid's context and context is stored inside this component's state
            if (
                prevProps.reviewPlanRows !== this.props.reviewPlanRows ||
                prevState.gridContext.doorTotals !== this.state.gridContext.doorTotals
            ) {
                if (this.state.resetPinnedRows) {
                    this.resetPinnedRows();
                } else {
                    this.refreshPinnedRows();
                }
            } else if (
                prevProps.dimensionsPanelApplied.metrics.showAssortmentPlan !==
                    this.props.dimensionsPanelApplied.metrics.showAssortmentPlan ||
                prevProps.reviewPlanRows !== this.props.reviewPlanRows
            ) {
                this.resetPinnedRows();
            }

            if (isGridRowGroupChanged || isDimensionFilterChanged) {
                this.setGridRowData(this.props.items);
                if (isGridRowGroupChanged && prevProps.rowGroup) {
                    this.columnApi?.setRowGroupColumns(this.props.rowGroup ?? []);
                }

                // When Metrics is enabled, row group will show subtotal that requires regeneration of column definitions,
                // ONLY the first time row group is enabled (!prevProps.rowGroup).
                const initialRowGroupOn = isGridRowGroupChanged && !prevProps.rowGroup;
                if (initialRowGroupOn && this.props.dimensionsPanelApplied.showDoors && assortmentDimensionMetrics) {
                    this.generateDefaultColumnDefsProps(this.props);
                }
            }

            if (isAggValuesChanged) {
                const colsAndFuncs = this.props.gridParams?.[GridParamType.AggFunc] ?? [];
                if (prevProps.gridParams?.[GridParamType.AggFunc]) {
                    colsAndFuncs.forEach(({ column, aggFunc }) => this.columnApi?.setColumnAggFunc(column, aggFunc));
                    this.columnApi?.setValueColumns(colsAndFuncs.map(({ column }) => column));
                }
            }
            //when we open the view than we need to clear the existing column label data.
            if (isPivotColsChanged && prevProps.pivot && this.props.pivot) {
                this.columnApi?.setPivotColumns(this.props.pivot.columns ?? []);
            }

            if (prevProps.pinnedRowColumnOrder !== this.props.pinnedRowColumnOrder) {
                this.generateDefaultColumnDefsProps(this.props, {});
            }

            if (
                this.isGridTileView &&
                this.props.tileGridContext.tileSettings !== prevProps.tileGridContext.tileSettings
            ) {
                this.calculateGridTileParams(this.columnApi);
                this.refreshTileViewAndPersistGroupsState();
            }

            if (
                (this.isGridTileView &&
                    this.props.tileGridContext.tileViewSize !== prevProps.tileGridContext.tileViewSize) ||
                (this.props.isServerRollup && prevProps.view !== this.props.view)
            ) {
                this.calculateGridTileParams(this.columnApi);
                this.refreshTileViewAndPersistGroupsState();
            }

            if (this.isGridTileView) {
                if (!prevProps.expandAllGroups && this.props.expandAllGroups) {
                    this.gridApi.refreshServerSideStore({ purge: false });
                }
            }
        }
    }

    getGridContext = (props: IProps): IGridContext => {
        const newDimensionFilters: string[][] = getNewDimensionFilters(
            props.allocationDimensions,
            props.gridParams,
            props.dimensionsPanelApplied
        );
        const {
            poiToItemsToSizeBulksMap,
            onFetchCollectionPOI,
            sizePacks,
            allocationBagRef,
            flags: { legacyCartEditableSizingGrid } = {},
        } = this.props;

        const attributeFilters = getAttributeFilters(props.gridParams);
        const doorIdsWithinClasifications = getDoorsWithinClasifications(props.doors, attributeFilters);

        return {
            dimensionFilters: newDimensionFilters,
            doorIdsWithinClasifications,
            doorTotals: props.doorTotals,
            doors: props.doors,
            doorTotalRowFiltered: props.doorTotalRowFiltered,
            hasDeliveries: !!props.dates?.length,
            poiToItemsToSizeBulksMap,
            onFetchCollectionPOI,
            sizePacks,
            clearRequestedItemId: () => {
                this.setState({
                    requestedItemId: undefined,
                });
            },
            getRequestedColumnId: () => this.state.requestedColumnId,
            getRequestedItemId: () => this.state.requestedItemId,
            getNoteValue: this.getNoteValue,
            onUpdateNote: (itemId: string, colId: string, value: string, node) => {
                this.handleUpdateNote({ itemId, colId, value, node });
            },
            onMutateBagValues: this.props.onMutateBagValues,
            allocationBagRef,
            editable: this.props.editable,
            isServerRollup: this.props.isServerRollup,
            tileGridContext: props.tileGridContext,
            activeValidationMode: props.activeValidationMode,
            itemsToBeValidated: props.itemsToBeValidated,
            exportStatuses: props.errorLog && buildExportToItemsToStatusMap(props.errorLog),
            onChangeErrorLogLocation: (params) => {
                this.handleViewErrorLog(props.location, props.router, params);
            },
            dataEntryType: this.props.dataEntryType,
            getFocusOnCellMap: this.props.onFocusOnCellMap,
            distributionTargets: this.props.distributionTargets,
            // assortment-manage-distribution flag condition
            // remove this condition when we remove the flag
            isDistributionEnabled: this.props.flags.assortmentManageDistribution,
        };
    };

    processCellForClipboard = ({ value, column, node }) => {
        const { colId, aggregationActive } = column;
        const { itemDisplayProperty = List([]) } = this.props;
        const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === colId);

        if (this.props.isServerRollup && aggregationActive) {
            return node.data.fields[`__agg_${colId}`].number_value.toString();
        }

        if (schemaField && schemaField.get("value_type") === ValueTypes.STRING) {
            const kind = schemaField.get("kind");
            switch (kind) {
                case Kinds.MULTILINE_TEXT:
                    value = (value || "").replace(/\n/gi, "<br/>");
                    break;
                case Kinds.SIZES_SELECTED:
                    // Add size range to clipboard
                    if (node) {
                        const sizeRange: string = get(node.data, Item.FIELDS.SELECTED_SIZE_RANGE, "");
                        if (sizeRange) {
                            value = sizeRange + SIZE_RANGE_DELIMITER + value;
                        }
                    }
                    break;
                default:
            }
            // if cell is string-y
            if (value.replace) {
                value = value.replace(/"/gi, "\\'"); // double quotes will cause cell shifts in ag-grid
            }
        }
        return value;
    };

    processCellFromClipboard = ({ value, column, node }) => {
        const { colId } = column;
        const {
            itemDisplayProperty = List([]),
            items,
            itemsId,
            sizeTableType,
            onMutateCollectionItem,
            onClearAllocations,
            onMutateDefaultItem,
        } = this.props;
        const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === colId);
        const valueType = schemaField && schemaField.get("value_type");
        if (valueType === ValueTypes.STRING) {
            const kind = schemaField.get("kind");
            let itemId, rowIndex, item;
            if (node.data?.type !== DEFAULT_VALUES_PINNED_ROW) {
                itemId = node.data.fields.id.string_value;
                rowIndex = this.getRowIndexById(itemId);
                item = items.get(rowIndex);
            } else {
                item = fromJS(node.data);
            }

            value = value.trim();

            switch (kind) {
                case Kinds.MULTILINE_TEXT:
                    value = (value || "").replace(/<br\/>/gi, "\n");
                    break;
                case Kinds.SIZE_PROFILE_SELECTED:
                    if (!value) {
                        const sizeCurveStatusField = itemDisplayProperty
                            .find((field) => field && field.get("kind") === Kinds.SIZE_CURVE_STATUS)
                            .toJS();

                        if (sizeCurveStatusField) {
                            item = item.setIn(
                                ["fields", sizeCurveStatusField.field_name, "string_value"],
                                SizeCurveStatus.None
                            );
                            process.nextTick(() => onMutateCollectionItem(itemsId, rowIndex, item));
                        }
                    }
                    break;
                case Kinds.SIZES_SELECTED:
                    const sizeManufactured = sizeManufacturedGetter(null, node);
                    const { sizeCurveMethodField, sizeCurveStatusField, sizeCurveField } =
                        getSizeRelatedFields(itemDisplayProperty);
                    const packResultsField = itemDisplayProperty.find((c) => c.get("kind") === Kinds.PACKS_RESULTS);
                    const [sizeRange, selectedSizes] = value.split(SIZE_RANGE_DELIMITER);

                    if (selectedSizes) {
                        value = selectedSizes;
                        item = item.setIn(Item.FIELDS.SELECTED_SIZE_RANGE, sizeRange);
                    } else {
                        value = sizeRange;
                    }

                    // Set side method
                    let sizeCurveMethod = "";

                    if (value) {
                        const manufacturedSizes = sizeManufactured.split(",");
                        const copiedSizes = parseSelectedSizes(
                            value,
                            schemaField.get("field_value_delimiter") || ","
                        ).allSizes;

                        if (areAllItemsContainedInList(copiedSizes, manufacturedSizes)) {
                            sizeCurveMethod = SizeCurveMethod.VendorMatched;
                        } else {
                            sizeCurveMethod = SizeCurveMethod.Manual;
                        }
                    } else {
                        item = item.setIn(Item.FIELDS.SELECTED_SIZE_RANGE, "");
                    }

                    if (packResultsField) {
                        item = item.deleteIn(["fields", packResultsField.get("field_name"), "string_value"]);
                    }

                    if (sizeCurveMethodField) {
                        item = item.setIn(["fields", sizeCurveMethodField.field_name, "string_value"], sizeCurveMethod);
                    }

                    if (sizeCurveField) {
                        item = item.setIn(["fields", sizeCurveField.field_name, "string_value"], "");
                    }

                    if (sizeCurveStatusField) {
                        item = item.setIn(
                            ["fields", sizeCurveStatusField.field_name, "string_value"],
                            SizeCurveStatus.None
                        );
                    }

                    if (sizeTableType === SizeTableTypes.Grouped) {
                        item = item.remove(Item.BULKS[0]);
                    }

                    if (rowIndex >= 0) {
                        onClearAllocations(item.getIn(Item.FIELDS.ID));
                    } else if (item) {
                        item = item.deleteIn(Item.ALLOCATIONS);
                    }

                    item = item.setIn(["fields", colId, "string_value"], value);

                    // Note we are returning the original value so grid will not register it as a change and we can do it all as a transaction
                    // this will keep under / redo linked
                    value = get(node.data, ["fields", colId, "string_value"]);
                    process.nextTick(() =>
                        rowIndex >= 0
                            ? onMutateCollectionItem(itemsId, rowIndex, item)
                            : item && onMutateDefaultItem(item)
                    );
                    break;
                default:
                    value = (value || "").replace(/(<br\/>)+/g, " ").trim();
            }

            // if cell is string-y
            if (value && value.replace) {
                value = value.replace(/\\'/gi, '"'); // convert quotes back correctly
            }
        } else if (valueType === ValueTypes.NUMBER || (!valueType && isDoorColumn(column.colId))) {
            // number columns or any door column
            const input = parseNumber(value);
            return isValidNumber(input) ? input : undefined;
        } else if (valueType === ValueTypes.DATE) {
            const { defaultDateFormat } = this.props;
            if (isValidDateFormat(value, [defaultDateFormat], true)) {
                value = generateDateValueObject(value, undefined, [defaultDateFormat], true);
            } else {
                value = get(node.data, ["fields", colId, "date_value"]);
            }
        }
        return value;
    };

    handleUpdateNote({ itemId, colId, value, node }) {
        const { items, itemsId, onMutateCollectionItem } = this.props;
        const rowIndex = this.getRowIndexById(itemId);

        let item = items.get(rowIndex);
        set(node.data, ["fields", `${notePrefix}${colId}`, "string_value"], value);
        item = item.setIn(["fields", `${notePrefix}${colId}`, "string_value"], value);
        onMutateCollectionItem(itemsId, rowIndex, item);

        this.setState({
            requestedItemId: itemId,
            requestedColumnId: colId,
            selectedNode: node,
        });
    }

    handleColumnPinned = ({ pinned, column, columns }: ColumnPinnedEvent) => {
        SentryUtil.startTransaction(AssortmentTransactions.PinColumn);
        SentryUtil.startSpan(AssortmentTransactions.PinColumn, AssortmentEvents.PinColumn, { column: column?.getId() });
        SentryUtil.cancelTransaction(AssortmentTransactions.AddColumn); // We are never enabling and pinning a column at the same time,
        // so we can be sure any AddColumn transaction in here is invalid.
        this.visibleColumnsSpan = undefined;
        const { itemDisplayProperty = List([]), currentAllocationDimensionView } = this.props;
        const colId = column && column.getColId();
        const index = itemDisplayProperty.findIndex((displayProperty) => {
            const fieldName = displayProperty.get("field_name");
            return fieldName === colId && fieldName !== IMAGE_URL;
        });
        const hasServerPivot =
            this.props.isServerRollup && columns.some((it) => it.getColId().includes(COL_DEF_SEPARATOR));
        if (!itemDisplayProperty.get(index) || hasServerPivot) {
            return;
        }

        const updateItemDisplayPreference = itemDisplayProperty.setIn(
            [index, "grid_column", "pinned"],
            getPinnedDto(pinned)
        );
        this.props.onChangeCurrentView(
            currentAllocationDimensionView?.length
                ? updateItemDisplayPreference.push(
                      Map<string, any>({
                          field_name: ARTIFICAL_ALLOCATION_DIMENSIONS_NODE,
                          display: { hidden: false },
                          grid_column: {},
                          children: currentAllocationDimensionView,
                      })
                  )
                : updateItemDisplayPreference
        );
    };

    onGridSizeChanged = ({ columnApi }) => {
        const sidebarElement = document.querySelector(".ag-theme-balham .ag-side-bar .ag-side-buttons") as HTMLElement;
        const { isScrollVisible } = this.state;

        if (sidebarElement) {
            const thereIsScroll = sidebarElement.scrollHeight > sidebarElement.clientHeight;

            this.setState({
                sidebarHeight: sidebarElement.clientHeight,
                ...(thereIsScroll !== isScrollVisible && {
                    isScrollVisible: sidebarElement.scrollHeight > sidebarElement.clientHeight,
                }),
            });
        }

        this.calculateGridTileParams(columnApi);
    };

    handleCellKeyDown = ({ event }: any) => {
        const typedEvent = event as KeyboardEvent;
        const eventTarget = typedEvent.target as HTMLInputElement;
        const eventKey = typedEvent.key;
        const isDeletionKey = KEYBOARD_KEYS.BACKSPACE === eventKey || KEYBOARD_KEYS.DELETE === eventKey;
        if (isDeletionKey && eventTarget?.value === undefined && this.props.editable) {
            resetSelectedCellRanges(this.gridApi);
        }
    };

    handleCutCopyPasteActions = (path: UnitsPath, action: CopyCutPasteActions) => {
        const { dates, items, onMutateCollectionItem, itemsId } = this.props;
        const { allocationClipboardState } = this.state;
        const srcIsDoor = isDoor({ path, dates });
        let newState = { path, action, srcIsDoor };

        if (action === CopyCutPasteActions.Paste) {
            const { path: statePath, action: stateAction } = allocationClipboardState ?? {};
            this.gridApi.forEachNodeAfterFilterAndSort((rowNode) => {
                const { data } = rowNode;
                if (data) {
                    const index = this.getIndexById(items, amphibiousGetIn(data, ProductDisplayFieldPaths.FIELDS.ID));
                    if (index >= 0) {
                        const newItem = Object.values(ItemBagType).reduce(
                            (acc, curr) =>
                                (stateAction === CopyCutPasteActions.Copy ? copyAndPasteUnits : cutAndPasteUnits)(
                                    acc,
                                    statePath,
                                    path,
                                    curr
                                ),
                            items.get(index)
                        );

                        onMutateCollectionItem(
                            itemsId,
                            index,
                            newItem.setIn(ProductDisplayFieldPaths.FIELDS.SIZE_CURVE_STATUS, SizeCurveStatus.Manual)
                        );
                    }
                }
            });
            newState = undefined;

            const dimensionType = srcIsDoor ? "door" : "delivery";
            const bagType = ItemFields.Bulks;
            const productCount = this.gridApi.getDisplayedRowCount();
            global.analytics.track(AllocationDimensionClipboardActions[stateAction], {
                dimensionType,
                bagType,
                productCount,
            });
        }
        this.setState({ allocationClipboardState: newState });
    };

    expandCollapseColumnGroups = (columnGroups: ColumnGroup[], expand: boolean): void => {
        for (const columnGroup of columnGroups) {
            columnGroup.setExpanded(expand);
            this.columnApi.setColumnGroupOpened(columnGroup.getGroupId(), expand);
        }
    };

    handleGetMainMenuItems: GetMainMenuItems = ({ defaultItems, column }) => {
        if (column.getColId() === IMAGE_URL_FIELD_NAME) {
            let currentLevel = 0;
            const columnDefs = this.gridApi.getColumnDefs();
            const firstLevelColumnGroups = getColumnGroupsAtLevel(this.columnApi, columnDefs, 0, currentLevel);
            const isFirstLevelGroupExpanded = isColumnGroupsExpanded(firstLevelColumnGroups);

            const initialMenuItems = [
                {
                    name: formatMessage("Deliveries") + (isFirstLevelGroupExpanded ? " <" : " >"),
                    action: () => {
                        this.expandCollapseColumnGroups(firstLevelColumnGroups, !isFirstLevelGroupExpanded);
                    },
                },
            ];
            const { dimensionsPanelApplied } = this.props;

            const activeAttributes = dimensionsPanelApplied.attributes.filter((attribute) => attribute.selected);
            const hasDoorGrouping = activeAttributes.length > 0;

            let doorGroupingMenuItems = [];
            if (hasDoorGrouping) {
                doorGroupingMenuItems = activeAttributes.map((attribute) => {
                    // Find corresponding door group name from classification
                    const foundSchemaClassification = this.props.doorClassificationSchema.find(
                        (classification) => classification.field_name === attribute.id
                    );
                    let name: string = attribute.id;
                    if (foundSchemaClassification) {
                        name = foundSchemaClassification.display_name;
                    }

                    currentLevel++;
                    const currentLevelColumnGroups = getColumnGroupsAtLevel(
                        this.columnApi,
                        columnDefs,
                        0,
                        currentLevel
                    );
                    const isCurrentLevelGroupExpanded = isColumnGroupsExpanded(currentLevelColumnGroups);

                    return {
                        name: formatMessage(name) + (isCurrentLevelGroupExpanded ? " <" : " >"),
                        action: () => {
                            this.expandCollapseColumnGroups(currentLevelColumnGroups, !isCurrentLevelGroupExpanded);
                        },
                    };
                });
            }

            return [...initialMenuItems, ...doorGroupingMenuItems];
        }

        const { allocationClipboardState } = this.state;
        const { allocationBagRef } = this.props;
        if (!allocationBagRef?.allocationBag) {
            return (
                copyCutPasteMenuHandler(
                    !allocationClipboardState,
                    !!allocationClipboardState?.srcIsDoor,
                    column,
                    this.handleCutCopyPasteActions
                ) ?? defaultItems
            );
        }

        const colDef = column.getColDef();
        const { groupId = (colDef as ColGroupDef).groupId, doorId } = colDef.cellRendererParams || {};

        // For columns that are not door and not deliveries, return default menu
        if (!groupId && !doorId) {
            return defaultItems;
        }

        // groupId and doorId is sufficient to track cut/copy source, e.g. clipboard: { groupId: 27, doorId: 1, action: Copy }
        return [
            {
                name: formatMessage("Copy Units"),
                action: () => this.setState({ clipboard: { groupId, doorId, action: CopyCutPasteActions.Copy } }),
            },
            {
                name: formatMessage("Cut Units"),
                action: () => this.setState({ clipboard: { groupId, doorId, action: CopyCutPasteActions.Cut } }),
            },
            {
                name: formatMessage("Paste Units"),
                action: () => this.pasteAllocation({ groupId, doorId }),
                disabled: !this.state.clipboard,
            },
        ];
    };

    pasteAllocation = ({ groupId: targetGroup, doorId: targetDoor }) => {
        const { items, itemsId, allocationBagRef, doors, onMutateCollectionItem, onMutateBagValues } = this.props;
        const {
            clipboard: { groupId: sourceGroup, doorId: sourceDoor, action },
        } = this.state;

        if (sourceGroup === targetGroup && sourceDoor === targetDoor) {
            return; // Do nothing if target == source
        }

        // We distinguish door from delivery by checking value of doorId/sourceDoor, which is undefined for delivery
        // E.g. Single column: sourceDoor: 3 -> doorOptions: [3]
        // E.g. Multiple columns: sourceDoor: undfined -> doorOptions: [1,2,...,38,39] from `doors`
        const doorOptions = sourceDoor ? [sourceDoor] : doors.map((door) => door.id);

        // Paste by looping through: 1. filtered items 2. each column/door
        this.gridApi.forEachNodeAfterFilterAndSort(({ data }) => {
            const itemId = amphibiousGetIn(data, ProductDisplayFieldPaths.FIELDS.ID);
            const index = this.getIndexById(items, itemId);
            const item = items.get(index).delete(ItemFields.Allocation).delete(ItemFields.Bulks);
            onMutateCollectionItem(
                itemsId,
                index,
                item.setIn(ProductDisplayFieldPaths.FIELDS.SIZE_CURVE_STATUS, SizeCurveStatus.Manual)
            );

            const bagType = AllocationBagType.bulks;
            doorOptions.forEach((sourceDoor) => {
                // Net-a-portre or Nordstrom Designer only have doors, no deliveries
                // In that case, sourceGroup and targetGroup will be empty string "" and filtered out
                const sourcePath = [sourceGroup, sourceDoor].filter((val) => val !== "");
                const targetPath = [targetGroup, targetDoor ? targetDoor : sourceDoor].filter((val) => val !== "");

                const quantity = getAllocationBagValue(
                    bagType,
                    allocationBagRef?.allocationBag as unknown as AllocationBag,
                    itemId,
                    sourcePath
                );
                onMutateBagValues({ bagType, rowId: itemId, dimensionValues: targetPath, quantity });

                if (action === CopyCutPasteActions.Cut) {
                    onMutateBagValues({ bagType, rowId: itemId, dimensionValues: sourcePath, quantity: 0 });
                }
            });
        });
        this.setState({ clipboard: undefined });
    };

    handleCellClicked = (e: CellClickedEvent) => {
        // can be prevented in the collapse/expand click handler.
        if (e.event.defaultPrevented) {
            return;
        }
    };

    handleNewColumnsLoaded = (_event) => {
        const { scrollToDoorsColumnOnLoad, setScrollToDoorsColumnOnLoad } = this.props;
        if (this.columnApi && scrollToDoorsColumnOnLoad) {
            const scrollContainerElement = document.querySelector(AG_GRID_SCROLLABLE_EL_SELECTOR);
            if (scrollContainerElement) {
                scrollContainerElement.scrollTo(
                    getTotalWidthFromNoDimensionsNoPinnedVisibleColumns(this.columnApi.getAllGridColumns()),
                    0
                );
            }
            setScrollToDoorsColumnOnLoad(false);
        }

        metricsTimerEnd();
    };

    render() {
        const {
            gridParams,
            toolpanelOpened,
            capabilities,
            isRollupMode,
            view,
            collectionId,
            shipmentReviewDetailRenderer,
            editable,
            dimensionsPanelApplied,
        } = this.props;
        const { columnDefs, sideBar, isScrollVisible, changedViewWhileOnRollup } = this.state;

        if (
            view !== View.History &&
            collectionId &&
            ((isRollupMode && !columnDefs.length) || (!isRollupMode && (!capabilities || !columnDefs.length)))
        ) {
            return <CircularProgress style={this.muiStyles.spinner} />;
        }
        const tileViewSize = this.props.tileGridContext?.tileViewSize;
        const tileViewCardWidth = TILE_VIEW_ITEM_SIZES[tileViewSize]?.WIDTH;
        const isE2E = "Cypress" in top;
        const assortmentDimensionMetrics = this.props.metricsEnabled;

        // Hackish: Investigate why rowRenderer is keeping controls instead of destroying them
        if (this.props.isServerRollup && changedViewWhileOnRollup) {
            return null;
        }

        return (
            <>
                <div
                    {...productCollectionGridViewStyles(tileViewCardWidth, gridParams?.rowGroup?.length)}
                    className={classNames("ag-theme-balham", "product-collection", {
                        toolbarLarge: toolpanelOpened === ToolPanels.Shop,
                        isScrollVisible,
                        tileMode: this.isGridTileView,
                    })}
                    data-test={DATATEST.ASSORTMENTS.GRID.GRID_WRAPPER}
                >
                    <AgGridReact
                        alignedGrids={this.props.getAlignedGrids()}
                        ref={this.mainGridRef}
                        aggFuncs={gridAggFunctions}
                        rowDragManaged={false}
                        masterDetail={shipmentReviewDetailRenderer !== undefined}
                        detailCellRendererParams={shipmentReviewDetailRenderer}
                        detailRowAutoHeight={true}
                        columnDefs={columnDefs}
                        onGridReady={this.onGridReady}
                        enableRangeSelection={true}
                        rowSelection="multiple"
                        enableCellChangeFlash={true}
                        suppressRowClickSelection={true} // if true, clicking rows doesn't select (useful for checkbox selection)
                        suppressColumnMoveAnimation
                        suppressMaxRenderedRowRestriction={isE2E}
                        rowBuffer={isE2E ? 500 : undefined}
                        sideBar={sideBar}
                        components={this.gridComponents}
                        defaultColDef={this.defaultColDef}
                        columnTypes={this.columnTypes}
                        suppressCopyRowsToClipboard={true}
                        rowHeight={shipmentReviewDetailRenderer !== undefined ? ROW_HEIGHT : undefined}
                        getRowHeight={shipmentReviewDetailRenderer !== undefined ? undefined : this.handleRowHeight}
                        onCellValueChanged={this.handleCellChange}
                        onColumnRowGroupChanged={this.handleColumnRowGroupChanged}
                        onColumnValueChanged={this.handleColumnValueChanged}
                        onColumnPivotChanged={this.handleColumnPivotChanged}
                        onColumnPivotModeChanged={this.handleColumnPivotModeChanged}
                        onFilterChanged={this.handleFilterChanged}
                        onSortChanged={this.handleSortChange}
                        onColumnVisible={this.handleColumnVisible}
                        onColumnResized={this.handleColumnResized}
                        onDragStopped={this.handleDragStopped}
                        onColumnMoved={this.handleColumnMoved}
                        getContextMenuItems={this.getContextMenuItems}
                        onRowDragEnd={this.handleRowDragEnd}
                        onRowDragEnter={this.handleRowDragStart}
                        onRowDragLeave={this.handleRowDragLeave}
                        onRowDragMove={this.handleRowDragMove}
                        onSelectionChanged={this.handleRowSelectionChange}
                        onToolPanelVisibleChanged={this.handleToolPanelVisibleChanged}
                        onColumnGroupOpened={this.handleColumnGroupOpened}
                        isExternalFilterPresent={this.isExternalFilterPresent}
                        overlayLoadingTemplate={`<span class="ag-overlay-loading-center">${formatMessage(
                            "Loading..."
                        )}</span>`}
                        overlayNoRowsTemplate={
                            this.props.isServerRollup
                                ? `<span class="ag-overlay-loading-center">${formatMessage("Loading...")}</span>`
                                : `<span>${formatMessage("No results found")}</span>`
                        }
                        debug={false}
                        getRowId={this.props.isServerRollup ? undefined : this.getRowId}
                        processCellForClipboard={this.processCellForClipboard as any}
                        processCellFromClipboard={this.processCellFromClipboard as any}
                        stopEditingWhenCellsLoseFocus={true}
                        onCellFocused={this.handleCellFocus}
                        excelStyles={this.excelStyles}
                        singleClickEdit={this.isMobileiOS}
                        suppressDragLeaveHidesColumns={true}
                        suppressMakeColumnVisibleAfterUnGroup={true}
                        statusBar={this.statusBar}
                        autoGroupColumnDef={this.autoGroupColumnDef}
                        onModelUpdated={this.handleModelUpdated}
                        icons={this.icons}
                        onColumnPinned={this.handleColumnPinned}
                        onFirstDataRendered={this.handleFirstDataRendered}
                        pivotMode={this.isGridTileView ? false : get(gridParams, [GridParamType.Pivot, "mode"], false)}
                        context={this.state.gridContext}
                        initialGroupOrderComparator={groupSortComparator}
                        onGridSizeChanged={this.onGridSizeChanged}
                        enterMovesDownAfterEdit={true}
                        onCellKeyDown={this.handleCellKeyDown}
                        valueCache={true}
                        allowDragFromColumnsToolPanel={true}
                        legacyComponentRendering={true}
                        onPinnedRowDataChanged={this.handlePinnedRowDataChange}
                        suppressPropertyNamesCheck={true}
                        tooltipShowDelay={200}
                        getMainMenuItems={this.handleGetMainMenuItems}
                        onCellClicked={this.handleCellClicked}
                        onNewColumnsLoaded={this.handleNewColumnsLoaded}
                        enableFillHandle={editable}
                        fillOperation={handleFillOperation}
                        suppressClearOnFillReduction={true}
                        debounceVerticalScrollbar
                        gridOptions={{
                            onGridColumnsChanged: ({ api }) => api.hideOverlay(),
                        }}
                        tabToNextCell={handleTabToNextCell}
                        {...(this.props.isServerRollup
                            ? {
                                  rowModelType: "serverSide",
                                  paginationPageSize: assortmentDimensionMetrics
                                      ? ROLLUP_DOOR_METRICS_SIZE_PAGE
                                      : ROLLUP_SIZE_PAGE,
                                  cacheBlockSize: assortmentDimensionMetrics
                                      ? ROLLUP_DOOR_METRICS_SIZE_PAGE
                                      : ROLLUP_SIZE_PAGE,
                                  headerHeight: this.isGridTileView ? 0 : 32,
                                  floatingFiltersHeight: this.isGridTileView ? 0 : null,
                                  suppressRowTransform: this.isGridTileView,
                                  isFullWidthRow: () => this.isGridTileView,
                                  fullWidthCellRenderer: "fullWidthCellRenderer",
                                  groupRowRendererParams: { innerRenderer: "groupRowInnerRenderer" },
                                  getRowStyle: this.handleRowStyle,
                                  blockLoadDebounceMillis: 600,
                                  serverSideStoreType: "partial",
                              }
                            : { doesExternalFilterPass: this.doesExternalFilterPass })}
                    />
                </div>
            </>
        );
    }

    private handleViewParamsChange = (gridParams, updatedDimensionsState, search, shouldLoadNewSavedView) => {
        /* This condition handles two scenarios :
        1) when a view is opened, previous column label should be cleared
        2) when the view selection has no column label, then the previous selection should not be shown
    */
        if (
            (gridParams !== undefined &&
                gridParams.pivot !== undefined &&
                gridParams.pivot.columns !== undefined &&
                gridParams.pivot.columns.length === 0) ||
            (gridParams !== undefined && gridParams.pivot === undefined)
        ) {
            this.columnApi?.setPivotColumns([]);
        }
        this.props.onViewParamsChange(gridParams, updatedDimensionsState, search, shouldLoadNewSavedView);
    };

    private handleSideBarDef = (props = this.props): SideBarDef => {
        const shouldShowProductsTab =
            this.props.collectionId && !props.isRollupMode && this.props.canShopBrand && props.view !== View.History;

        return {
            toolPanels: [
                {
                    id: ToolPanels.Columns,
                    labelDefault: formatMessage("Columns"),
                    labelKey: "columns",
                    iconKey: "columns",
                    toolPanel: "agColumnsToolPanel",
                    ...(this.isGridTileView
                        ? {
                              toolPanelParams: {
                                  suppressPivots: true,
                                  suppressPivotMode: true,
                                  suppressSyncLayoutWithGrid: true,
                              },
                          }
                        : { toolPanelParams: { suppressSyncLayoutWithGrid: false, suppressColumnExpandAll: true } }),
                },
                ...(props.flags?.dimensionsToolPanel && props.allocationEnabled
                    ? [
                          {
                              id: ToolPanels.Dimension,
                              labelDefault: formatMessage("Doors"),
                              labelKey: "dimensions",
                              iconKey: "dimensionsPanel",
                              toolPanel: "dimensionsToolPanel",
                              width: 300,
                              toolPanelParams: {
                                  isRollupMode: props.isRollupMode,
                                  isServerRollup: props.isServerRollup,
                                  editable: props.editable,
                                  onEditDoorGroupingRequest: props.onEditDoorGroupingRequest,
                                  onImportAttributesToggle: props.onImportDoorAttributes,
                                  sidebarHeight: this.state?.sidebarHeight,
                                  setScrollToDoorsColumnOnLoad: this.props.setScrollToDoorsColumnOnLoad,
                                  allocationBagRef: this.props.allocationBagRef,
                              },
                          },
                      ]
                    : []),
                {
                    id: ToolPanels.Filters,
                    labelDefault: formatMessage("Filters"),
                    labelKey: "filters",
                    iconKey: "filter",
                    toolPanel: "agFiltersToolPanel",
                    toolPanelParams: {
                        suppressSyncLayoutWithGrid: true,
                    },
                },
                ...(this.props.collectionId
                    ? [
                          {
                              id: ToolPanels.Smu,
                              labelDefault: formatMessage("SMU Filter"),
                              labelKey: "smuToolPanel",
                              iconKey: "filter",
                              toolPanel: "smuToolPanel",
                              toolPanelParams: {
                                  onFilterAssortmentBySMU: (value: SMU_FILTER) => {
                                      this.props.onFilterAssortmentBySMU(value);
                                  },
                                  filterAssortmentBySMU: () => this.props.filterAssortmentBySMU,
                              },
                          },
                      ]
                    : []),
                {
                    id: ToolPanels.Sort,
                    labelDefault: formatMessage("Sort"),
                    labelKey: "sortToolPanel",
                    iconKey: "sortPanel",
                    toolPanel: "sortToolPanel",
                },
                ...(this.props.collectionId
                    ? [
                          {
                              id: ToolPanels.Views,
                              labelDefault: formatMessage("Views"),
                              labelKey: "dataViewPanel",
                              iconKey: "dataViewsPanel",
                              toolPanel: "dataViewPanel",
                              toolPanelParams: {
                                  isRollupMode: props.isRollupMode,
                                  collectionId: this.props.collectionId,
                                  switchingTile: this.props.switchingTile,
                                  handleSwitchingTileAndGrid: this.props.handleSwitchingTileAndGrid,
                                  onSetImageCellDimensions: this.props.onSetImageCellDimensions,
                                  onViewParamsChange: this.handleViewParamsChange,
                                  onClearViewParams: this.props.onClearViewParams,
                                  fetchSavedViewData: this.props.fetchSavedViewData,
                                  getGridParams: () => {
                                      return {
                                          ...this.props.gridParams,
                                          search: this.props.searchText,
                                      };
                                  },
                                  editable: this.props.editable,
                              },
                          },
                      ]
                    : []),
                ...(shouldShowProductsTab
                    ? [
                          {
                              id: ToolPanels.Shop,
                              labelDefault: formatMessage("Products"),
                              labelKey: "shopToolPanel",
                              iconKey: "shopPanel",
                              toolPanel: "shopToolPanel",
                          },
                      ]
                    : []),
            ],
            defaultToolPanel: this.props.toolpanelOpened || ToolPanels.Columns,
        };
    };

    private calculateGridTileParams = (columnApi: ColumnApi) => {
        if (!this.props.isServerRollup) {
            return;
        }

        const tileViewSize = this.props.tileGridContext.tileViewSize;
        const tileViewCardWidth = TILE_VIEW_ITEM_SIZES[tileViewSize].WIDTH;
        const { startingXPosition: prevStartingXPosition } = this.state;
        // @ts-ignore `columnModel` is private.
        const { viewportRight: containerWidth } = columnApi.columnModel;
        const cardsPerRow = Math.floor(containerWidth / (tileViewCardWidth + 2 * TILE_GUTTER));
        const startingXPosition = (containerWidth - (tileViewCardWidth + 2 * TILE_GUTTER) * cardsPerRow) / 2;

        prevStartingXPosition !== startingXPosition &&
            this.setState({
                cardsPerRow,
                startingXPosition,
            });
    };

    private handleRowHeight = (params) => {
        if (params.node.rowPinned) {
            return 28;
        }

        if (this.isGridTileView && params.data) {
            if (params.node.group) {
                return TILE_GROUP_HEIGHT;
            }

            const { cardsPerRow } = this.state;
            const { rowIndex, lastRow } = params.data;
            const { height: tileViewCardHeight } = this.getCurrentTileViewCellDimensions();

            if (lastRow) {
                const pendingRow = (rowIndex + 1) % cardsPerRow;
                if (pendingRow > 0) {
                    return pendingRow === 1
                        ? tileViewCardHeight
                        : (tileViewCardHeight / cardsPerRow) * (cardsPerRow - pendingRow + 1);
                }
            }
            return tileViewCardHeight / cardsPerRow;
        }

        if (params.node.group) {
            return ROW_HEIGHT;
        }

        return this.props.imageCellDimensions?.height || ROW_HEIGHT;
    };

    private handleRowStyle = ({ node }) => {
        if (this.isGridTileView && !node.group) {
            const { cardsPerRow, startingXPosition } = this.state;
            const tileViewSize = this.props.tileGridContext.tileViewSize;
            const tileViewCardWidth = TILE_VIEW_ITEM_SIZES[tileViewSize].WIDTH;
            const { height: tileViewCardHeight } = this.getCurrentTileViewCellDimensions();
            const parentIndex = node.parent.rowIndex || 0;
            const indexIncrement = node.parent.level === -1 ? 1 : 0; // This is needed where there is no group
            const rowIndex = node.rowIndex - parentIndex + indexIncrement;
            const chunkNumber = Math.ceil(rowIndex / cardsPerRow);
            const leftConvertionRate = rowIndex - (chunkNumber - 1) * cardsPerRow;

            const rowHeight = tileViewCardHeight / cardsPerRow;
            const rowTop =
                node.rowHeight === rowHeight
                    ? node.rowTop - node.rowHeight * (leftConvertionRate - 1)
                    : node.rowTop - rowHeight * (leftConvertionRate - 1);

            return {
                left: `${
                    startingXPosition +
                    (tileViewCardWidth * (leftConvertionRate - 1) + 2 * TILE_GUTTER * (leftConvertionRate - 1))
                }px`,
                top: `${rowTop}px`,
                height: `${tileViewCardHeight}px`,
            };
        }
        return undefined;
    };

    private getCurrentTileViewCellDimensions() {
        const tileViewSize = this.props.tileGridContext.tileViewSize;
        const tileSettings = this.props.tileGridContext.tileSettings;
        const { height, width } = getTileViewCellDimensions(tileViewSize, tileSettings);

        return { height, width };
    }

    private handleModelUpdated = debounce(({ api }: ModelUpdatedEvent) => {
        !this.props.isServerRollup && this.props.onSetTileViewData(getViewDataFromApi(api), getDataFromApi(api));
        if (this.state.searchTokens.length > 0) {
            process.nextTick(() => {
                SentryUtil.finishSpan(AssortmentTransactions.Search, AssortmentEvents.Search);
                SentryUtil.finishTransaction(AssortmentTransactions.Search);
            });
        }

        if (this.isGridTileView) {
            this.redrawRows(api);
        }
    }, 50); // We debounce here as onModelUpdated is called on cell change, sort, filtering and colDef change

    private redrawRows = (api) => {
        const rows = [];
        api.forEachNode((node) => {
            if (!node.group) {
                rows.push(node);
            }
        });

        process.nextTick(() => {
            rows.length && api.redrawRows({ rowNodes: rows });
        });
    };

    private handleFirstDataRendered = ({ api }) => {
        if (this.props.onFirstDataRendered && !this.props.isServerRollup) {
            this.props.onFirstDataRendered(getViewDataFromApi(api));
        }
    };

    private getRowIndexById = (id) => {
        // gets row index determined by collection not ag-grid
        return this.props.items?.findIndex((item) => item.getIn(Item.FIELDS.ID) === id);
    };

    private handleCellFocus = ({ rowIndex, column }) => {
        let rowId: string;
        if (rowIndex !== null && this.gridApi) {
            const row = this.gridApi.getDisplayedRowAtIndex(rowIndex);
            if (row) {
                rowId = get(row.data, Item.FIELDS.ID);
            }
        }
        const { onFocusChange, itemsId, collectionId } = this.props;
        if (itemsId && collectionId) {
            let field = ""; // if there is no focus, communicate that
            if (rowId && column !== null) {
                const { colId } = column;
                field = `${rowId}.${colId}`;
            }
            if (this.focusField !== field) {
                onFocusChange(collectionId, itemsId, field);
                this.focusField = field;
            }
        }
    };

    private handleFilterChanged = (params) => {
        const { api } = params;

        // presence of afterFloatingFilter for indicating whether came from filter
        if (params.hasOwnProperty("afterFloatingFilter")) {
            let filter = this.getFilterModel();

            // We are updating the filterModel manually only after refresh the page and the filters are loaded async.
            // This is only for ServerRollup
            if (this.props.isServerRollup && !this.initialFiltersLoaded && this.thereAreInitialFilters) {
                filter = this.props.filter;
                this.gridApi.setFilterModel(filter);
                this.initialFiltersLoaded = true;
            }
            const { gridParams } = this.props;
            const dimensionFilters = getDimensionFilters(gridParams);
            this.props.onGridParamsChange({
                type: SaveStateToGridQueryParamsType.SetFilters,
                value: {
                    ...filter,
                    // add custom door filters are are not part of ag-grid filters
                    ...dimensionFiltersToAgGridFilters(dimensionFilters),
                },
            });
        }

        if (this.props.onFilterChanged && !this.props.isServerRollup) {
            this.props.onFilterChanged(getViewDataFromApi(api));
        }
        this.setState({ allocationClipboardState: undefined });
    };

    private handleSortChange = ({ api, columnApi }: SortChangedEvent) => {
        const sort = getSortModelFromApi(columnApi);
        api.setSuppressRowDrag(sort.length > 0);
        this.props.onGridParamsChange({
            type: SaveStateToGridQueryParamsType.SetSort,
            value: sort,
        });
    };

    private tokenizeSearchTerm(searchTerm) {
        if (!searchTerm) {
            return [];
        }
        return searchTerm.toUpperCase().split(" ");
    }

    private onGridReady = (params: GridReadyEvent) => {
        this.gridApi = params.api;
        this.columnApi = params.columnApi;
        // TODO: Metrics for column selection
        // AgGrid modules don't expose ToolPanelColumnComp anymore, so it is difficult to monkey-patch the
        // listener dispatched when a checkbox changes.

        if (this.props.onGridMount) {
            this.props.onGridMount(params.api, params.columnApi, this.mainGridRef);
        }

        if (this.props.searchText) {
            const newTokens = this.tokenizeSearchTerm(this.props.searchText);
            const existing = this.state.searchTokens;
            // In theory this can only happen if user has refreshed the page with existing search term set
            // The prop check fails, so we just need 1 additional check here to ensure
            // grid re-renders with the tokenized search term
            if (newTokens.toString() !== existing.toString()) {
                this.setState({
                    searchTokens: this.tokenizeSearchTerm(this.props.searchText),
                });
            }
        }
        const { allocationBagRef } = this.props;
        const rowData = this.getRowData(this.props.items, allocationBagRef?.allocationBag);
        if (!rowData?.length) {
            this.handleFirstDataRendered({ api: params.api });
        }
        if (!this.props.isServerRollup) {
            if (this.props.view !== View.History || rowData?.length) {
                this.gridApi.setRowData(rowData);
            }
        }
        this.setServerSideDatasource();
        this.setInitialGridParams();
        this.props.onRowDataSet();

        this.debouncedSetFilterModel();

        this.calculateGridTileParams(params.columnApi);

        this.props.isServerRollup && window.addEventListener("resize", this.debouncedUpdateTileViewAfterResizeWindow);

        if (this.props.onGridReady) {
            this.props.onGridReady(params);
        }
    };

    private handleGetPlanByDoor = (door: string) => {
        const { doorTotals } = this.props;
        const actual = doorTotals?.[door];

        return {
            actual: actual || {},
            isLoading: actual === undefined,
        };
    };

    private setInitialGridParams = () => {
        const { location, onSearchChange, filter, sort } = this.props;
        const { search } = location.query || {};

        if (filter) {
            this.gridApi.setFilterModel(filter);
        }

        if (this.props.isServerRollup) {
            this.thereAreInitialFilters = !!filter;
        }

        if (search) {
            onSearchChange(search);
        }

        if (sort) {
            this.columnApi.applyColumnState(sort);
        }
    };

    private getExpandedRowsKeyPath(node) {
        const path = [node.key];
        let parent = node.parent;

        while (parent && parent.level > -1) {
            path.push(parent.key);
            parent = parent.parent;
        }
        return path;
    }

    // Persisting the expanded state of groups before to refresh AgGrid
    private refreshTileViewAndPersistGroupsState() {
        const previousExpandedRowsGroupKeyPath = [];

        if (this.isGridTileView && this.gridApi) {
            this.gridApi.forEachNode((node) => {
                if (node.expanded && node.group) {
                    if ((node.parent.key && node.parent.expanded) || !node.parent.key) {
                        previousExpandedRowsGroupKeyPath.push(this.getExpandedRowsKeyPath(node));
                    } else {
                        node.setExpanded(false);
                    }
                }
            });
            this.setState({ previousExpandedRowsGroupKeyPath });
            this.debouncedHandleFilterChanged();
        }
    }

    // Validate if node should be expanded based on `previousExpandedRowsGroupKeyPath`
    private validateIfNodeShouldBeExpanded = (node) => {
        const { previousExpandedRowsGroupKeyPath } = this.state;
        const filteredPaths = previousExpandedRowsGroupKeyPath.filter((path) => path && path[0] === node.key);

        if (filteredPaths.length === 0) {
            return false;
        }

        let isValid = false;
        let currentNodePath;
        filteredPaths.forEach((path) => {
            let currentNode = node;
            for (let index = 0; index < path.length; index++) {
                if (currentNode.key === path[index]) {
                    currentNode = currentNode.parent;

                    // Make `isValid` true when is the last iteration and the last parent node is the root node
                    if (index === path.length - 1 && currentNode.level === -1) {
                        isValid = true;
                        currentNodePath = path;
                    }
                }
            }
        });

        return { isValid, currentNodePath };
    };

    // Expand rows group based on last state stored in `previousExpandedRowsGroupKeyPath`
    private expandPreviousRowsGroup = () => {
        const { previousExpandedRowsGroupKeyPath } = this.state;

        if (previousExpandedRowsGroupKeyPath.length > 0) {
            this.gridApi.forEachNode((node) => {
                if (!node.expanded) {
                    const { isValid, currentNodePath } = this.validateIfNodeShouldBeExpanded(node) as any;

                    if (isValid) {
                        this.expandedRowGroups.push(currentNodePath);
                        node.setExpanded(true);

                        if (this.expandedRowGroups.length === previousExpandedRowsGroupKeyPath.length) {
                            this.setState({ previousExpandedRowsGroupKeyPath: [] });
                            this.expandedRowGroups = [];
                        } else {
                            this.requestToExpandNestedGroups = false;
                        }
                    } else if (node.parent.level !== -1 && !this.requestToExpandNestedGroups) {
                        this.requestToExpandNestedGroups = true;
                        this.expandPreviousRowsGroup();
                    }
                }
            });
        }
    };

    private expandOne = () => {
        let unexpandedNodeId: string = null;
        this.gridApi.forEachNode((node) => {
            if (node.group && !node.expanded && !unexpandedNodeId) {
                unexpandedNodeId = node.id;
            }
        });
        if (unexpandedNodeId) {
            const node = this.gridApi.getRowNode(unexpandedNodeId);
            node.setExpanded(true);
        }
    };

    private setServerSideDatasource = () => {
        if (!this.props.isServerRollup) {
            return;
        }

        const that = this;

        let prevColDefHash: string = null;
        let prevPivtoFieldsHash: string = null;
        let prevValuesFieldsHash: string = null;
        let prevPivotMode: boolean = null;
        const blankTextValue = getBlankTextValue();
        const allocationDimensions = this.props.allocationDimensions;
        const rollupDoorDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Door);
        const rollupDateDimension = getAllocationDimensionByIndex(allocationDimensions, AllocationDimensionIndex.Date);
        let doorDimensionId, dateDimensionId;
        const rollupAllocationDimensions = [];

        if (rollupDateDimension?.id) {
            // rollupDateDimension ID is a combination of allocationDimensionID and collectionID
            dateDimensionId = rollupDateDimension.id?.split(ROLLUP_ALLOCATION_DIMENSION_DELIMITER)[0];
            rollupAllocationDimensions.push({
                ...rollupDateDimension,
                id: dateDimensionId,
            });
        }

        if (rollupDoorDimension?.id) {
            // rollupDoorDimension ID is a combination of allocationDimensionID and collectionID
            doorDimensionId = rollupDoorDimension.id?.split(ROLLUP_ALLOCATION_DIMENSION_DELIMITER)[0];
            rollupAllocationDimensions.push({
                ...rollupDoorDimension,
                id: doorDimensionId,
            });
        }

        const serverRollupEndpoint = getServerRollupEndpoint(this.props.collectionId);
        const collectionsRefs = this.props.collection.getIn(["collections_refs"]);

        const extractProps = () => ({
            ...this.props,
            rollupAllocationDimensions,
        });
        const extractStateProps = () => this.state;
        const onExpandPreviousRowsGroup = () => {
            if (this.isGridTileView) {
                this.expandPreviousRowsGroup();
            }
        };
        const extractDimensionFilters = (gridParams) => getDimensionFilters(gridParams);
        const extractAttributesFilters = (gridParams) => getAttributeFilters(gridParams);
        const setSearchAfterTokens = (groupKeys: string[], token: string[]) => {
            this.setState(({ searchAfterTokens }) => {
                return {
                    searchAfterTokens: !groupKeys.length
                        ? {
                              [SEARCH_AFTER_TOKEN_KEY]: token,
                          }
                        : {
                              ...searchAfterTokens,
                              [groupKeys.join(",")]: token,
                          },
                };
            });
        };
        const createFieldsHash = (fields: ColumnVO[], includeAggreation?: boolean) =>
            fields.map((f) => (includeAggreation ? `${f.field}-${f.aggFunc}` : f.field)).join(COL_DEF_SEPARATOR);

        const addPivotColDefs = (pivotColDefs) => {
            const colDefHash = createColsHash(pivotColDefs);
            if (prevColDefHash !== colDefHash) {
                this.columnApi.setSecondaryColumns(pivotColDefs);
                prevColDefHash = colDefHash;
            }
        };

        const { itemDisplayProperty } = this.props;
        const idpFields: { [key: string]: ISchemaKey } = itemDisplayProperty.toJS().reduce((acc, schema) => {
            acc[schema.field_name] = schema;
            return acc;
        }, {});
        const deliveryIds = this.props.dates.map((date) => date.id);
        const doorIds = this.props.doors.map((door) => door.id);
        const hasDeliveries = !!deliveryIds.length;

        const startReCreateSnapshot = (merchantId: string, collectionIds: string[]) => {
            const collectionDriveItemId = this.props.collectionDriveItem.id;

            collectionIds.length && this.props.onStartCreateRollupSnapshot?.();

            this.props.onFetchCollectionDriveItem?.(merchantId, collectionDriveItemId, collectionIds);
        };

        const checkIfNeedRecreateRollupSnapshot = (error: any, merchantId: string) => {
            if (error.message === ROLLUP_INDEX_NOT_FOUND_MESSAGE) {
                const collectionIds =
                    Object.keys(collectionsRefs?.map(({ collection_id }) => collection_id)?.toJS()) ?? [];
                startReCreateSnapshot(merchantId, collectionIds);
            }
        };

        const datasource = {
            getRows({ request, successCallback, failCallback }: IServerSideGetRowsParams) {
                const {
                    doors: allDoors,
                    onSetDoorTotals,
                    onSetFilteredDoorTotals,
                    onSetDoorTotalRow,
                    dimensionsPanelApplied,
                    gridParams,
                    filterAssortmentBySMU,
                    merchantId,
                    onSnackbarAdd,
                    recreateServerRollupSnapshot,
                    defaultDateFormat,
                    rollupAllocationDimensions,
                    sort,
                    view,
                    showTotals,
                    doorClassificationSchema,
                    location,
                    merchantAllocationDimensions,
                    metricsEnabled,
                } = extractProps();
                if (recreateServerRollupSnapshot) {
                    successCallback([], 0);
                    return;
                }
                const { searchTokens, searchAfterTokens } = extractStateProps();
                let dateFilters, doorFilters;
                let dimensionFilters = extractDimensionFilters(gridParams);
                let selectedCollectionId;
                const viewIdQuery = location.query?.viewId;
                if (viewIdQuery) {
                    const [_, collectionId] = viewIdQuery.split(CollectionSavedViewRollupSeparator);
                    selectedCollectionId = collectionId;
                }
                const attributeFilters = transformDoorAttributesFiltersForRollup(
                    selectedCollectionId,
                    extractAttributesFilters(gridParams),
                    doorClassificationSchema
                );
                const hasAttributesfilters = Object.keys(attributeFilters || {}).length > 0;
                const doors = getVisibleDoors(allDoors, dimensionsPanelApplied, gridParams);
                const doorIdsWithinClassification =
                    hasAttributesfilters && getDoorsWithinClasifications(doors, attributeFilters);
                const attributeDoorsFilters =
                    hasAttributesfilters && getAttributesDoorsFilter(allDoors, attributeFilters);
                const doorGroupsDimensions = [[], []];

                if (rollupAllocationDimensions.length === 2) {
                    dateFilters = dimensionFilters[0]?.filter((it) => !nullish(it));
                    doorFilters = dimensionFilters[1]?.filter((it) => !nullish(it));
                    doorGroupsDimensions[0] = getDoorGroupDimensions(
                        deliveryIds,
                        dateFilters,
                        dimensionsPanelApplied.dimensions[0]?.values
                            .filter((dimensionValue) => dimensionValue.selected)
                            .map((dimensionValue) => dimensionValue.id)
                    );
                    doorGroupsDimensions[1] = getDoorGroupDimensions(
                        doorIds,
                        doorFilters,
                        dimensionsPanelApplied.dimensions[1]?.values
                            .filter((dimensionValue) => dimensionValue.selected)
                            .map((dimensionValue) => dimensionValue.id)
                    );
                } else if (rollupAllocationDimensions.length === 1) {
                    doorFilters = dimensionFilters[0]?.filter((it) => !nullish(it));
                    doorGroupsDimensions[1] = getDoorGroupDimensions(
                        doorIds,
                        doorFilters,
                        dimensionsPanelApplied.dimensions[0]?.values
                            .filter((dimensionValue) => dimensionValue.selected)
                            .map((dimensionValue) => dimensionValue.id)
                    );
                }

                const hasClientMetrics = hasClientDoorMetrics(dimensionsPanelApplied.metrics.values);
                const hasServerMetrics = hasServerDoorMetrics(dimensionsPanelApplied.metrics.values);
                const doorMetrics = getDoorMetricsSelected(dimensionsPanelApplied.metrics.values, true);
                doorGroupsDimensions[1] = doorIdsWithinClassification
                    ? doorIdsWithinClassification.filter((id) => doorGroupsDimensions[1].includes(id))
                    : doorGroupsDimensions[1];

                dimensionFilters = [!dateFilters ? [] : dateFilters, !doorFilters ? [] : doorFilters];
                const groupBy = getGroupByColumn(request);
                const pivotActive = isPivotEnabled(request);

                let regeneratePivotColumns = false;
                if (request.pivotMode) {
                    const pivotFieldsHash = createFieldsHash(request.pivotCols);
                    const valuesFieldsHash = createFieldsHash(request.valueCols, true);
                    if (prevPivtoFieldsHash !== pivotFieldsHash || prevValuesFieldsHash !== valuesFieldsHash) {
                        prevPivtoFieldsHash = pivotFieldsHash;
                        prevValuesFieldsHash = valuesFieldsHash;
                        regeneratePivotColumns = true;
                    }
                }

                if (!!prevPivotMode !== !!request.pivotMode) {
                    prevPivotMode = request.pivotMode;
                    regeneratePivotColumns = true;
                }

                if ((request.pivotMode && request.pivotCols.length === 0) || view === View.Tile) {
                    regeneratePivotColumns = false;
                }

                const calculateTotals = request.groupKeys?.length === 0 && request.startRow === 0;
                const assortmentDimensionMetrics = metricsEnabled;
                const calculateDoorMetrics =
                    assortmentDimensionMetrics && dimensionsPanelApplied.showDoors && !!doorMetrics?.length;
                const isOffsetPagination = request.rowGroupCols.length !== request.groupKeys.length;
                const query = buildServerRollupQuery({
                    merchantId,
                    sortModel: regeneratePivotColumns ? null : request.sortModel.length > 0 ? request.sortModel : sort,
                    filterModel: request.filterModel,
                    rowGroupCols: request.rowGroupCols,
                    groupKeys: request.groupKeys.map((key) => (key === blankTextValue ? "" : key)),
                    valueCols: request.valueCols,
                    pivotCols: pivotActive ? request.pivotCols : [],
                    size: request.endRow - request.startRow,
                    dimensionFilters,
                    searchTokens,
                    itemDisplayProperty,
                    calculateTotals,
                    calculateDoorMetrics,
                    totalKind: assortmentDimensionMetrics
                        ? RollupTotalKind.DoorMetricsTotal
                        : RollupTotalKind.FooterTotal,
                    filterAssortmentBySMU,
                    dateDimensionId,
                    doorDimensionId,
                    merchantDateFormat: defaultDateFormat,
                    deliveryIds,
                    doorIds,
                    doorIdsWithinClassification,
                    attributeDoorsFilters,
                    useAggSorting: true,
                    currentGroup: groupBy,
                    offset: isOffsetPagination && request.startRow > 0 ? request.startRow : null,
                    searchAfter:
                        !isOffsetPagination && request.startRow > 0
                            ? request.groupKeys.length
                                ? searchAfterTokens[request.groupKeys.join(",")]
                                : searchAfterTokens[SEARCH_AFTER_TOKEN_KEY]
                            : [],
                    rollupAllocationDimensions,
                    doorMetrics,
                    attributes: dimensionsPanelApplied.attributes,
                    doorGroupsDimensions,
                });

                if (pivotActive && regeneratePivotColumns) {
                    const pivotQuery = buildServerRollupQuery({
                        merchantId,
                        sortModel: regeneratePivotColumns
                            ? null
                            : request.sortModel.length > 0
                              ? request.sortModel
                              : sort,
                        filterModel: request.filterModel,
                        valueCols: request.valueCols,
                        pivotCols: request.pivotCols,
                        size: ROLLUP_PIVOT_SIZE_PAGE,
                        dimensionFilters,
                        searchTokens,
                        itemDisplayProperty,
                        calculateTotals: false,
                        filterAssortmentBySMU,
                        dateDimensionId,
                        doorDimensionId,
                        merchantDateFormat: defaultDateFormat,
                        deliveryIds,
                    });

                    fetchJSON(serverRollupEndpoint, {
                        method: "POST",
                        body: JSON.stringify(pivotQuery),
                    })
                        .then((response: IServerRollupResponse) => {
                            let checkCollectionRefs = request.pivotCols.some(
                                (pivot) => pivot.field === ASSORTMENT_FIELD_NAME
                            );
                            checkCollectionRefs =
                                checkCollectionRefs ||
                                request.valueCols.some((pivot) => pivot.field === ASSORTMENT_FIELD_NAME);
                            const { pivot_columns } = response.meta;
                            const enhancedPivots = enhanceSecondaryColDefs(
                                pivot_columns,
                                request.valueCols,
                                request.pivotCols,
                                idpFields,
                                defaultDateFormat,
                                checkCollectionRefs ? collectionsRefs : null
                            );
                            regeneratePivotColumns && addPivotColDefs(enhancedPivots);
                        })
                        .catch((error) => {
                            checkIfNeedRecreateRollupSnapshot(error, merchantId);
                            Sentry.captureException(error);
                            onSnackbarAdd({ message: formatMessage("There was an error fetching pivot columns") });
                        });
                }

                fetchJSON(serverRollupEndpoint, {
                    method: "POST",
                    body: JSON.stringify(query),
                })
                    .then((response: IServerRollupResponse) => {
                        const isDimensionGroup =
                            isOffsetPagination &&
                            request.rowGroupCols?.[request.groupKeys.length]?.field?.startsWith(
                                ALLOCATION_DIMENSION_ID_PREFIX
                            );

                        let rows: any =
                            !groupBy && !pivotActive
                                ? readRows(
                                      response,
                                      request.startRow,
                                      collectionsRefs,
                                      itemDisplayProperty,
                                      null,
                                      request.valueCols,
                                      query.door_groups,
                                      hasDeliveries
                                  )
                                : readGroupedRows(
                                      merchantAllocationDimensions,
                                      isDimensionGroup
                                          ? {
                                                ...response,
                                                group_by: response.group_by.slice(request.startRow, request.endRow),
                                            }
                                          : response,
                                      groupBy,
                                      request,
                                      itemDisplayProperty,
                                      pivotActive,
                                      collectionsRefs,
                                      query.door_groups,
                                      hasDeliveries
                                  );

                        const isGroupOpening = request.groupKeys?.length > 0;
                        const currentGroupKeyIndex = request.groupKeys?.length - 1;
                        const allocationDimensionGroups = request.rowGroupCols.filter((groupItem) =>
                            isAllocationDimensionId(groupItem.id)
                        );
                        const hasDoorsAndDeliveries = allocationDimensionGroups.length === 2;
                        const hasEmptyGroupKey = request.groupKeys?.some((groupKey) => groupKey === "");

                        if (isGroupOpening && hasDoorsAndDeliveries && hasEmptyGroupKey) {
                            // Create an empty row for the second allocation dimension group
                            if (response.items.length && groupBy) {
                                rows = readRows(
                                    response,
                                    request.startRow,
                                    collectionsRefs,
                                    itemDisplayProperty,
                                    null,
                                    request.valueCols
                                );
                                rows = [{ ...rows[0], itemCount: rows.length }];
                                // make sure collection name isn't displaying in grouped row
                                rows[0].fields.assortment_name = undefined;
                            } else if (
                                response.group_by.length > 1 &&
                                isAllocationDimensionId(request.rowGroupCols[currentGroupKeyIndex + 1].id)
                            ) {
                                rows = [{ ...rows[0], itemCount: rows.length }];
                                // make sure collection name isn't displaying in grouped row
                                rows[0].fields.assortment_name = undefined;
                            }
                        }
                        // NU-19080: Get only doors included in the filters when group by doors
                        if (groupBy?.id === doorDimensionId) {
                            rows = getOnlyDoorGroupedRows(rows, doorFilters, groupBy);
                        }

                        const hasMorePages = rows.length >= request.endRow - request.startRow;
                        let lastRow = request.startRow + rows.length;
                        lastRow = (response.search_after || isOffsetPagination) && hasMorePages ? -1 : lastRow;
                        lastRow !== -1 && rows.length && ((rows[rows.length - 1] as any).lastRow = true);

                        const sortModel = request.sortModel.length > 0 ? request.sortModel : sort;

                        if (sortModel?.length > 0) {
                            let sortGroup;
                            if (sortModel[0].colId === AG_GRID_AUTOCOLUM && isAllocationDimensionId(groupBy?.id)) {
                                sortGroup = {
                                    sort: sortModel[0].sort,
                                    colId: groupBy?.id,
                                };
                            } else {
                                sortGroup = request.sortModel.find((field) => field.colId === groupBy?.id);
                            }

                            // verify if current sorting is by allocation dimension
                            if (isAllocationDimensionId(sortGroup?.colId)) {
                                rows = sortAllocationDimensionsGroupedRows(sortGroup, rows, rollupAllocationDimensions);
                            }
                        }

                        if (!isGroupOpening) {
                            rows = updateTotalCalculationsInRows(
                                rows ?? [],
                                request.valueCols,
                                response.group_by ?? [],
                                itemDisplayProperty,
                                allocationDimensionGroups.length > 0,
                                pivotActive ? request.pivotCols : []
                            );
                        }

                        // clear pivot definitions when it is disabled
                        !pivotActive && regeneratePivotColumns && addPivotColDefs([]);
                        if (calculateTotals && !assortmentDimensionMetrics) {
                            const doorTotalQuery = buildServerRollupQuery({
                                merchantId,
                                sortModel: regeneratePivotColumns ? null : request.sortModel,
                                filterModel: request.filterModel,
                                rowGroupCols: request.rowGroupCols,
                                groupKeys: request.groupKeys.map((key) => (key === blankTextValue ? "" : key)),
                                filterAssortmentBySMU,
                                searchTokens,
                                dimensionFilters,
                                size: 1,
                                itemDisplayProperty,
                                dateDimensionId,
                                doorDimensionId,
                                calculateTotals: true,
                                totalKind: RollupTotalKind.DoorTotal,
                                merchantDateFormat: defaultDateFormat,
                                deliveryIds,
                            });

                            fetchJSON<IServerRollupResponse>(serverRollupEndpoint, {
                                method: "POST",
                                body: JSON.stringify(doorTotalQuery),
                            })
                                .then((dtResponse: IServerRollupResponse) => {
                                    const doorTotalsResponse = readDoorTotals(
                                        doors,
                                        {
                                            ...dtResponse,
                                            meta: response.meta,
                                        },
                                        doorFilters || dateFilters
                                    );
                                    calculateTotals && onSetFilteredDoorTotals?.(doorTotalsResponse);
                                })
                                .catch((error) => {
                                    checkIfNeedRecreateRollupSnapshot(error, merchantId);
                                    Sentry.captureException(error);
                                    onSnackbarAdd({
                                        message: formatMessage("There was an error fetching door totals totals"),
                                    });
                                    return null;
                                });
                        }

                        if (
                            doorMetrics.length > 0 &&
                            calculateTotals &&
                            calculateDoorMetrics &&
                            assortmentDimensionMetrics
                        ) {
                            const totalRowQueryPromises = [];
                            if (hasClientMetrics) {
                                const totalRowQuery = buildServerRollupQuery({
                                    merchantId,
                                    sortModel: regeneratePivotColumns ? null : request.sortModel,
                                    filterModel: request.filterModel,
                                    rowGroupCols: request.rowGroupCols,
                                    groupKeys: request.groupKeys.map((key) => (key === blankTextValue ? "" : key)),
                                    filterAssortmentBySMU,
                                    searchTokens,
                                    dimensionFilters,
                                    size: 1,
                                    itemDisplayProperty,
                                    dateDimensionId,
                                    doorDimensionId,
                                    calculateTotals: true,
                                    calculateDoorMetrics,
                                    totalKind: RollupTotalKind.DoorTotalRow,
                                    merchantDateFormat: defaultDateFormat,
                                    deliveryIds,
                                    doorIds,
                                    rollupAllocationDimensions,
                                    doorMetrics: doorMetrics.filter((it) => SERVER_DOOR_METRICS.includes(it.kind)), // We only need the total_row for the client door metrics
                                    attributes: dimensionsPanelApplied.attributes,
                                    doorGroupsDimensions,
                                });

                                const clientMetricPromise = fetchJSON<IServerRollupResponse>(serverRollupEndpoint, {
                                    method: "POST",
                                    body: JSON.stringify(totalRowQuery),
                                })
                                    .then((trResponse: IServerRollupResponse) => {
                                        const totalRowData = readDoorMetrics(
                                            trResponse.meta.door_metrics_total,
                                            totalRowQuery.door_groups,
                                            hasDeliveries
                                        );
                                        onSetDoorTotalRow?.(totalRowData);

                                        if (showTotals && !hasServerMetrics) {
                                            that.resetPinnedRows({
                                                doorMetricsTotal: trResponse.meta.door_metrics_total,
                                                doorGroups: totalRowQuery.door_groups,
                                                hasDeliveries,
                                            });
                                        }
                                    })
                                    .catch((error) => {
                                        checkIfNeedRecreateRollupSnapshot(error, merchantId);
                                        Sentry.captureException(error);
                                        onSnackbarAdd({
                                            message: formatMessage("There was an error fetching total rows"),
                                        });
                                        return null;
                                    });

                                totalRowQueryPromises.push(clientMetricPromise);
                            }

                            if (hasServerMetrics && showTotals) {
                                const totalRowQuery = buildServerRollupQuery({
                                    merchantId,
                                    sortModel: regeneratePivotColumns
                                        ? null
                                        : request.sortModel.length > 0
                                          ? request.sortModel
                                          : sort,
                                    filterModel: request.filterModel,
                                    rowGroupCols: request.rowGroupCols,
                                    groupKeys: request.groupKeys.map((key) => (key === blankTextValue ? "" : key)),
                                    assortmentTotals: true,
                                    dimensionFilters,
                                    searchTokens,
                                    itemDisplayProperty,
                                    filterAssortmentBySMU,
                                    dateDimensionId,
                                    doorDimensionId,
                                    calculateDoorMetrics,
                                    merchantDateFormat: defaultDateFormat,
                                    deliveryIds,
                                    doorIds,
                                    size: 1,
                                    doorMetrics,
                                    attributes: dimensionsPanelApplied.attributes,
                                    doorGroupsDimensions,
                                    rollupAllocationDimensions,
                                    totalKind: RollupTotalKind.DoorMetricsTotal,
                                    attributeDoorsFilters,
                                });

                                const serverMetricPromise = fetchJSON(serverRollupEndpoint, {
                                    method: "POST",
                                    body: JSON.stringify(totalRowQuery),
                                })
                                    .then((response: IServerRollupResponse) => {
                                        that.resetPinnedRows({
                                            doorMetricsTotal: response.meta.door_metrics_total,
                                            doorGroups: totalRowQuery.door_groups,
                                            hasDeliveries,
                                        });
                                    })
                                    .catch((error) => {
                                        checkIfNeedRecreateRollupSnapshot(error, merchantId);
                                        Sentry.captureException(error);
                                        failCallback();
                                        onSnackbarAdd({ message: formatMessage("There was an error fetching items") });
                                    });

                                totalRowQueryPromises.push(serverMetricPromise);
                            }

                            Promise.all(totalRowQueryPromises).finally(() => {
                                successCallback(rows, lastRow);
                            });
                        } else {
                            successCallback(rows, lastRow);
                        }

                        setSearchAfterTokens(request.groupKeys, response.search_after);
                        onExpandPreviousRowsGroup();

                        if (that.isGridTileView && that.props.expandAllGroups) {
                            that.expandOne();
                        }
                    })
                    .catch((error) => {
                        checkIfNeedRecreateRollupSnapshot(error, merchantId);

                        Sentry.captureException(error);
                        failCallback();
                        onSnackbarAdd({ message: formatMessage("There was an error fetching items") });
                    });

                if (calculateTotals) {
                    const isThereAFilter =
                        query.filter || query.search || query.delivery_filter?.length || query.door_filter?.length;
                    if (isThereAFilter && assortmentDimensionMetrics) {
                        const filteredTotalQuery = buildServerRollupQuery({
                            merchantId,
                            sortModel: regeneratePivotColumns ? null : request.sortModel,
                            filterModel: request.filterModel,
                            rowGroupCols: request.rowGroupCols,
                            groupKeys: request.groupKeys.map((key) => (key === blankTextValue ? "" : key)),
                            filterAssortmentBySMU,
                            searchTokens,
                            dimensionFilters,
                            size: 1,
                            itemDisplayProperty,
                            dateDimensionId,
                            doorDimensionId,
                            calculateTotals: true,
                            totalKind: RollupTotalKind.FooterTotal,
                            merchantDateFormat: defaultDateFormat,
                            deliveryIds,
                        });

                        fetchJSON(serverRollupEndpoint, {
                            method: "POST",
                            body: JSON.stringify(filteredTotalQuery),
                        })
                            .then((response: IServerRollupResponse) => {
                                const doorTotals = readDoorTotals(doors, response);
                                calculateTotals && onSetFilteredDoorTotals?.(doorTotals);
                            })
                            .catch((error) => {
                                checkIfNeedRecreateRollupSnapshot(error, merchantId);
                                Sentry.captureException(error);
                                onSnackbarAdd({
                                    message: formatMessage("There was an error fetching filtered totals"),
                                });
                            });
                    }

                    const totalsQuery = buildServerRollupQuery({
                        merchantId,
                        sortModel: regeneratePivotColumns ? null : request.sortModel,
                        size: 1,
                        itemDisplayProperty,
                        dateDimensionId,
                        doorDimensionId,
                        calculateTotals: true,
                        totalKind: RollupTotalKind.FooterTotal,
                        merchantDateFormat: defaultDateFormat,
                        deliveryIds,
                    });

                    fetchJSON(serverRollupEndpoint, {
                        method: "POST",
                        body: JSON.stringify(totalsQuery),
                    })
                        .then((response: IServerRollupResponse) => {
                            const doorTotals = readDoorTotals(doors, response);
                            calculateTotals && onSetDoorTotals?.(doorTotals);
                            !isThereAFilter && assortmentDimensionMetrics && onSetFilteredDoorTotals?.(doorTotals);
                        })
                        .catch((error) => {
                            checkIfNeedRecreateRollupSnapshot(error, merchantId);
                            Sentry.captureException(error);
                            onSnackbarAdd({ message: formatMessage("There was an error fetching totals") });
                        });
                }
            },
        };

        this.gridApi.setServerSideDatasource(datasource);
    };

    private getRowId = (params: Pick<GetRowIdParams, "data">) => {
        const { data } = params;

        let id = get(data, "fields.id.string_value");

        const collectionId = this.props.isServerRollup && get(data, `fields.${COLLECTION_ID_FIELD_NAME}.string_value`);
        if (id === undefined && !this.tossedErrorForIndex) {
            this.tossedErrorForIndex = true;
            Sentry.captureException(new Error("Index doesn't exist for collection"));
        }

        // If this is Proxy row, suffix id, except proxyIndex = 0, who assumes as master row
        if (data.proxyIndex > 0) {
            id = `proxy#${data.proxyIndex}#${id}`;
        }

        return collectionId ? buildServerRollupRowNodeId(id, collectionId) : id;
    };

    private getSelectedRowIds(): OrderedSet<string> {
        return OrderedSet(this.gridApi ? this.gridApi.getSelectedNodes().map((node) => this.getRowId(node)) : []);
    }

    private getColIdSchemaMapping = memoizeOne(getFieldNameToSchemaMapping);

    private handleCellChange = (e: CellValueChangedEvent) => {
        const { oldValue, newValue, value, colDef, column, node, data, context } = e;
        const {
            itemDisplayProperty = List([]),
            itemsId,
            items,
            onClearAllocations,
            onMutateCollectionItem,
            onMutateDefaultItem,
            sizeRanges,
            onResetSizeRanges,
            onLookupAssortmentErrors,
            dimensionPlanMetrics,
            onMutateReviewPlan,
            onMutateMetricsReviewPlan,
            dates,
            allocationDimensions,
            isRollupMode,
            sizeTableType,
            sizePacks,
            sizingAllocationEnabled,
            onMutateBagValues,
        } = this.props;

        if (oldValue === newValue) {
            return;
        }
        if (node.rowPinned && data?.type !== DEFAULT_VALUES_PINNED_ROW) {
            // If a pinned row changed and it is not the default row, it is because it was a plan/totals row
            // in which case, only doors should've changed. And for doors, we store params in cellRendererParams
            const { cellRendererParams } = colDef;
            const { groupId, doorId, metric } = cellRendererParams;
            const dimenstionPath = buildPathBasedOnType(doorId, groupId);

            if (metric) {
                onMutateMetricsReviewPlan(dimenstionPath, metric.kind, newValue);
            } else {
                const totalType = getTotalType(node.rowIndex, dimensionPlanMetrics);
                onMutateReviewPlan(dimenstionPath, totalType, newValue);
            }

            return;
        }

        let item, rowIndex, itemId;
        if (data?.type !== DEFAULT_VALUES_PINNED_ROW) {
            itemId = data.fields.id.string_value;
            rowIndex = this.getRowIndexById(itemId);
            item = items.get(rowIndex);
        } else {
            item = fromJS(data);
        }

        const tv = getTypeAndValueBasedOnType(colDef.type, data?.fields[colDef.colId], value);

        if (this.itemCurrent !== item) {
            this.itemCurrent = item;
        }

        if (
            [
                ColumnTypes.DoorBulkQuantity,
                ColumnTypes.ClassificationTotal,
                ColumnTypes.AllocationDimensionTotal,
            ].includes(colDef.type as ColumnTypes) &&
            (data[Item.BULKS[0]] || this.props.allocationBagRef?.allocationBag)
        ) {
            let mutatedAllocationBag = this.props.allocationBagRef?.allocationBag;
            const isLegacyBag = !mutatedAllocationBag;
            // We are only storing to old bag if bagValue is not the new version
            if (isLegacyBag) {
                this.itemCurrent = this.itemCurrent.setIn(Item.BULKS, fromJS(data[Item.BULKS[0]]));
            }
            const itemVendorSizes = this.itemCurrent ? sizeManufacturedGetter(null, { data: this.itemCurrent }) : "";
            const itemSelectedsizes: string =
                this.itemCurrent && !isRollupMode
                    ? this.itemCurrent.getIn(ProductDisplayFieldPaths.FIELDS.SIZES_SELECTED, "")
                    : "";
            const hasOnlyOneVendorSize =
                sizingAllocationEnabled && delimitedFieldHasSingleValue(itemVendorSizes, MANUFACTURED_SIZES_DELIMITER);

            const { curveSizes, sizesAddedManually, allSizes } = parseSelectedSizes(itemSelectedsizes);
            // When both Vendor Sizes and Selected Sizes have the same single size, we apply one-sizing logic of copying bulks into allocations
            if (hasOnlyOneVendorSize && allSizes.length === 1 && allSizes[0] === itemVendorSizes) {
                if (isLegacyBag) {
                    const newAllocations = {
                        r: legacyDeriveSingleSizeAllocationsFromBulks(data[Item.BULKS[0]], dates, itemVendorSizes),
                    };
                    this.itemCurrent = this.itemCurrent.setIn(Item.ALLOCATIONS, newAllocations);
                    data[Item.ALLOCATIONS[0]] = newAllocations;
                } else {
                    mutatedAllocationBag = syncAllocationsWithBulks(
                        context.allocationBagRef.allocationBag,
                        itemId,
                        itemVendorSizes
                    );
                    const allocationBagMutations = generateReconciliationBagMutations(
                        context.allocationBagRef.allocationBag,
                        mutatedAllocationBag,
                        itemId,
                        AllocationBagType.allocations
                    );
                    allocationBagMutations.forEach((mutation) => {
                        onMutateBagValues(mutation);
                    });
                }
            }
            const { sizeCurve, sizeCurveStatus } = getUpdatedSizesCurveAndStatus(
                fromJS(data),
                curveSizes,
                sizesAddedManually,
                allocationDimensions,
                mutatedAllocationBag
            );

            if (this.itemCurrent.getIn(ProductDisplayFieldPaths.FIELDS.SIZE_CURVE) !== sizeCurve) {
                this.itemCurrent = this.itemCurrent.setIn(ProductDisplayFieldPaths.FIELDS.SIZE_CURVE, sizeCurve);
            }
            if (this.itemCurrent.getIn(ProductDisplayFieldPaths.FIELDS.SIZE_CURVE_STATUS) !== sizeCurveStatus) {
                this.itemCurrent = this.itemCurrent.setIn(
                    ProductDisplayFieldPaths.FIELDS.SIZE_CURVE_STATUS,
                    sizeCurveStatus
                );
            }

            const analytics = (global as any).analytics as any;

            analytics?.track(DOOR_UNIT_CHANGED, {
                location: Location.Grid,
                doorId: colDef.cellRendererParams.doorId, // Same logic: params are on cellRendererParams
                groupId: column.getParent()?.getGroupId,
                headerName: colDef.headerName,
                colId: column.getColId(),
            });
        } else {
            const oldSizeRange = this.itemCurrent.getIn(Item.FIELDS.SIZES_SELECTED);
            const oldUpcRange = this.itemCurrent.getIn(Item.FIELDS.UPC);

            this.itemCurrent = this.itemCurrent.setIn(
                ["fields", colDef.field || colDef.colId],
                fromJS({ [tv.t]: tv.v })
            );
            const isSMU = !item.getIn(Item.FIELDS.PRODUCT_ID);

            const newSizeRange = this.itemCurrent.getIn(Item.FIELDS.SIZES_SELECTED);
            if (isSMU) {
                if (oldSizeRange !== newSizeRange) {
                    const newUpcValues = getNewUpcValues(oldSizeRange, newSizeRange, oldUpcRange);
                    this.itemCurrent = this.itemCurrent.setIn(Item.FIELDS.UPC, newUpcValues);
                }
            }
            const analytics = (global as any).analytics as any;
            analytics.track(GRID_CELL_CHANGE, {
                colId: column.getColId(),
                name: colDef.headerName,
            });
        }

        if (colDef.cellEditor === Editors.DropdownSizesEditor) {
            const rawItemDisplayProperty = itemDisplayProperty.toJS();
            const sizeManufactured = sizeManufacturedGetter(null, node);
            const schemaField = itemDisplayProperty.find((field) => field && field.get("field_name") === colDef.colId);
            const { allSizes: selectedSizes } = parseSelectedSizes(
                value,
                schemaField.get("field_value_delimiter") || ","
            );
            const { sizeCurveMethodField, sizeCurveStatusField, sizeCurveField } =
                getSizeRelatedFields(itemDisplayProperty);
            const { sizeCurveMethod, sizeCurve, sizeCurveStatus, shouldResetSizes } =
                getSizesFieldsToUpdateFromSelectedSizes(
                    selectedSizes as string[],
                    sizeRanges,
                    rawItemDisplayProperty,
                    sizeManufactured
                );
            const packResultsField = itemDisplayProperty.find((c) => c.get("kind") === Kinds.PACKS_RESULTS);
            if (shouldResetSizes) {
                onResetSizeRanges();
            }

            // Update size method and clear size curve, size status, pack results and allocations
            if (packResultsField) {
                this.itemCurrent = this.itemCurrent.deleteIn([
                    "fields",
                    packResultsField.get("field_name"),
                    "string_value",
                ]);
            }
            if (sizeCurveMethodField) {
                this.itemCurrent = this.itemCurrent.setIn(
                    ["fields", sizeCurveMethodField.field_name, "string_value"],
                    sizeCurveMethod
                );
            }

            if (sizeCurveField) {
                this.itemCurrent = this.itemCurrent.setIn(
                    ["fields", sizeCurveField.field_name, "string_value"],
                    sizeCurve
                );
            }

            if (sizeCurveStatusField) {
                this.itemCurrent = this.itemCurrent.setIn(
                    ["fields", sizeCurveStatusField.field_name, "string_value"],
                    sizeCurveStatus
                );
            }

            if (rowIndex >= 0) {
                onClearAllocations(this.itemCurrent.getIn(Item.FIELDS.ID));
            } else if (item) {
                if (context.allocationBagRef && itemId) {
                    context.allocationBagRef.allocationBag = clearBagValues(
                        AllocationBagType.allocations,
                        context.allocationBagRef.allocationBag,
                        itemId,
                        (type, itemId, breadcrumb, value) => {
                            onMutateBagValues({
                                bagType: type,
                                dimensionValues: breadcrumb,
                                rowId: itemId,
                                quantity: value,
                            });
                        }
                    );
                } else if (!this.props.allocationBagRef) {
                    this.itemCurrent = this.itemCurrent.deleteIn(Item.ALLOCATIONS);
                }
            }

            if (sizeTableType === SizeTableTypes.Grouped) {
                if (context.allocationBagRef && itemId) {
                    context.allocationBagRef.allocationBag = clearBagValues(
                        AllocationBagType.bulks,
                        context.allocationBagRef.allocationBag,
                        itemId,
                        (type, itemId, breadcrumb, value) => {
                            onMutateBagValues({
                                bagType: type,
                                dimensionValues: breadcrumb,
                                rowId: itemId,
                                quantity: value,
                            });
                        }
                    );
                } else if (!this.props.allocationBagRef) {
                    this.itemCurrent = this.itemCurrent.remove(Item.BULKS[0]);
                }
            }

            if (newValue === "") {
                this.itemCurrent = this.itemCurrent.setIn(Item.FIELDS.SELECTED_SIZE_RANGE, "");

                if (sizeCurveMethodField) {
                    this.itemCurrent = this.itemCurrent.setIn(
                        ["fields", sizeCurveMethodField.field_name, "string_value"],
                        ""
                    );
                }
            }
        }

        const colIdToSchemaMapping = this.getColIdSchemaMapping(itemDisplayProperty);
        const schemaField = colIdToSchemaMapping.get(colDef.colId);
        const schemaFieldKind = schemaField?.get("kind");

        if (Kinds.SIZE_PROFILE_SELECTED === schemaFieldKind) {
            const { sizeCurveStatusField } = getSizeRelatedFields(itemDisplayProperty);
            const sizeCurveStatusPath = ["fields", sizeCurveStatusField.field_name, "string_value"];
            this.itemCurrent.getIn(sizeCurveStatusPath) !== SizeCurveStatus.Manual &&
                (this.itemCurrent = this.itemCurrent.setIn(sizeCurveStatusPath, SizeCurveStatus.Manual));
        }

        if (
            [Kinds.PACKS_MAX_PER_DOOR, Kinds.PACKS_MAX_PER_PROD, Kinds.PACKS_SELECTED, Kinds.PACKS_TYPE].includes(
                schemaFieldKind
            )
        ) {
            const rawItemDisplayProperty = itemDisplayProperty.toJS();
            const relevantSchemas = {
                packsPerDoorSchema: findOneFieldByKind(Kinds.PACKS_MAX_PER_DOOR, rawItemDisplayProperty),
                packsPerProductSchema: findOneFieldByKind(Kinds.PACKS_MAX_PER_PROD, rawItemDisplayProperty),
            };

            this.itemCurrent = changeSizePackField(
                colDef.colId,
                this.itemCurrent,
                newValue,
                sizePacks,
                relevantSchemas
            );

            const { sizeCurveStatusField } = getSizeRelatedFields(itemDisplayProperty);
            if ([Kinds.PACKS_SELECTED, Kinds.PACKS_TYPE].includes(schemaFieldKind) && sizeCurveStatusField) {
                this.itemCurrent = this.itemCurrent.setIn(
                    ["fields", sizeCurveStatusField.field_name, "string_value"],
                    SizeCurveStatus.None
                );
            }
        }

        if (rowIndex >= 0) {
            // We don't need to mutate if nothing changes
            if (this.itemCurrent !== item) {
                onMutateCollectionItem(itemsId, rowIndex, this.itemCurrent, tv.v, undefined, colDef.field);

                if (
                    schemaField &&
                    [Kinds.SIZE_CURVE_STATUS, Kinds.SIZE_CURVE_METHOD, Kinds.SIZES_SELECTED].includes(schemaFieldKind)
                ) {
                    if (!(schemaFieldKind === Kinds.SIZES_SELECTED && sizeTableType === SizeTableTypes.Grouped)) {
                        onLookupAssortmentErrors();
                    }
                }
            }
        } else if (item) {
            onMutateDefaultItem(this.itemCurrent, tv.v);
        }
    };

    private handleColumnVisible = ({ columns, columnApi }: ColumnVisibleEvent) => {
        // We are forcing image column to always be visible at the end of this function, thus triggering this handler.
        // In that case, skip it.
        const onlyImageColumn = columns.length === 1 && columns[0].getColId() === IMAGE_URL;
        const hasServerPivot =
            this.props.isServerRollup && columns.some((column) => column.getColId().includes(COL_DEF_SEPARATOR));

        // `isResetColumns` is generally on saved view change and newer schema update (on load).
        // In such case, don't execute below as it can interfere by unnecessary-ly itemDisplayProperty update.
        if (!this.props.isResetColumns && !onlyImageColumn && !hasServerPivot) {
            if (this.visibleColumnsSpan) {
                this.visibleColumnsSpan.setData(
                    "columns",
                    columns.map((c: Column) => c.getId())
                );
                this.visibleColumnsSpan = undefined;
            }

            const { itemDisplayProperty = List([]) } = this.props;
            const newSchemaKeys = itemDisplayProperty.map((schemaKey) => {
                const column = columns.find((col) => schemaKey && schemaKey.get("field_name") === col.getColId());
                if (column && column.getColId() !== IMAGE_URL) {
                    // dont allow image_url to be toggled
                    return schemaKey.setIn(["display", "hidden"], !column.isVisible());
                } else {
                    return schemaKey;
                }
            });
            this._handleSaveGridLayout(newSchemaKeys);
        }
        // This will trigger another `handleColumnVisible` which we will skip.
        columnApi.setColumnVisible(IMAGE_URL, true);
    };

    private handleColumnResized = ({ columns, finished, source }) => {
        // `isResetColumns` is generally on saved view change and newer schema update (on load).
        // In such case, don't execute below as it can interfere by unnecessary-ly itemDisplayProperty update.
        if (!this.props.isResetColumns) {
            const { itemDisplayProperty = List([]) } = this.props;
            const sources = [AUTOSIZE_COLUMNS, UI_COLUMN_DRAGGED];
            if (!finished && columns && columns.length === 1) {
                this.setState({
                    resizing: columns,
                });
            } else if (finished && columns) {
                SentryUtil.startTransaction(AssortmentTransactions.ResizeColumn);
                SentryUtil.startSpan(AssortmentTransactions.ResizeColumn, AssortmentEvents.ResizeColumn, {
                    columns: columns.map((c: Column) => c.getId()),
                });

                if (sources.includes(source)) {
                    const { resizing } = this.state;
                    const resizedColumns = resizing.length ? resizing : columns;
                    const allocationDimensionFields = [];

                    /*Create a list of all dimension columns being resized and
                      capture their width */
                    resizedColumns.forEach((column) => {
                        if (isDoorColumn(column.colId, column.colDef.type)) {
                            const allocationDimensionSchemaField = {
                                field_name: column.colId,
                                grid_column: {
                                    width: column.actualWidth,
                                },
                            };
                            allocationDimensionFields.push(allocationDimensionSchemaField);
                        }
                    });

                    const columnSchemaFields = itemDisplayProperty.map((schemaField) => {
                        const column = resizedColumns.find(
                            (column) => schemaField && schemaField.get("field_name") === column.colId
                        );
                        if (column) {
                            // the row's height is updated only when the imageColumn is resized
                            if (schemaField.get("kind") === Kinds.IMAGE_URL) {
                                let actualWidth = column.actualWidth;
                                if (column.actualWidth <= column.minWidth + IMAGE_COLUMN_BUFFER) {
                                    actualWidth = column.minWidth;
                                }
                                const { width, height } = getNewResizedImageCellDimensions(actualWidth);
                                this.props.onSetImageCellDimensions({ width, height });
                                if (this.gridApi) {
                                    this.gridApi.resetRowHeights();
                                }
                            }

                            return schemaField.setIn(["grid_column", "width"], column.actualWidth);
                        }

                        return schemaField;
                    });
                    this.setState({ resizing: [] });
                    this._handleSaveGridLayout(columnSchemaFields, allocationDimensionFields);
                }
            }
        }
    };

    private handleColumnMoved = (e) => {
        if (!["gridOptionsChanged", "api"].includes(e.source)) {
            const { toIndex, column, columns } = e;
            this.setState({
                dragging: { toIndex, column, columns },
            });
        }
    };

    private isGridReady() {
        return !isEmpty(this.columnApi?.getAllColumns());
    }

    private handleColumnPivotModeChanged = ({ columnApi }) => {
        const { gridParams } = this.props;
        const mode = (columnApi as any).isPivotMode();

        const pivotSettings = get(gridParams, GridParamType.Pivot, { mode: false });
        if (pivotSettings.mode !== mode) {
            let pivotParams: any = { ...pivotSettings, mode };
            if (!pivotParams.mode && (!pivotParams.columns || pivotParams.columns.length === 0)) {
                pivotParams = undefined;
            }
            this.props.onGridParamsChange({
                type: SaveStateToGridQueryParamsType.SetPivot,
                value: { ...pivotSettings, mode },
            });
        }
    };

    private saveDoorsLayoutOnly = (columnAPI) => {
        const { dimensionsPanelApplied, doorClassificationSchema, flags } = this.props;

        const { attribute } = legacy_parseDimensionParams(dimensionsPanelApplied);
        if (columnAPI && dimensionsPanelApplied.showDoors) {
            let layout = [];
            if (this.props.dates?.length) {
                const groups = columnAPI.getAllDisplayedColumnGroups();
                layout = getResultingDoorsLayout(groups, this.props.dates);
            } else if (attribute) {
                const activeClassification = doorClassificationSchema.find((v) => v.field_name == attribute.id);
                if (activeClassification) {
                    const mainGroups = activeClassification?.constraint?.allowed_values.map((v) => ({
                        id: getGroupId(dimensionsPanelApplied.delimiter, "", v.string_value),
                    }));
                    const groups = columnAPI.getAllDisplayedColumnGroups();

                    layout = getResultingDoorsLayout(groups, mainGroups);
                }
            } else {
                layout = doorsOnlyLayout(columnAPI.getAllGridColumns());
            }

            this.handleSaveGridLayout(this.props.itemDisplayProperty, layout);
        }
        this.setState({ dragging: {} });
    };

    private handleDragStopped = (event: DragStoppedEvent) => {
        const { dragging } = this.state;
        if (dragging.column || dragging.columns) {
            SentryUtil.startTransaction(AssortmentTransactions.MoveColumn);
            SentryUtil.startSpan(AssortmentTransactions.MoveColumn, AssortmentEvents.MoveColumn, {
                columns: dragging.column ? [dragging.column.getId()] : dragging.columns?.map((c) => c?.getId()),
            });
        }
        const { itemDisplayProperty = List([]) } = this.props;
        if (dragging.column) {
            if (!dragging.column.parent || dragging.column.parent.children.length === 1) {
                // We ask here
                // if the column being dragged right now has no parent (in which case it can't be a door)
                // or if its parent has only 1 children (which would be this sole column).
                // We are safe on the assumption that [1] All the doors column groups have one total column.
                // If that is the case, we prove doors column groups have at least two children based on:
                // - [1] above.
                // - fix for NU-5255, which guarantees in all cases that in order for a column group to exist, there should
                //   be at least one door inside said group.
                const fromIndex = itemDisplayProperty.findIndex(
                    (key) => key && key.get("field_name") === dragging.column.colId
                );
                const { toIndex } = dragging;
                if (fromIndex > -1 && fromIndex !== toIndex) {
                    const updatedSchemaKeys = getUpdatedSchemaKeysByMovingColumns({
                        fromIndex,
                        toIndex,
                        itemDisplayProperty,
                    });
                    // Update gridView with new schema keys
                    this._handleSaveGridLayout(updatedSchemaKeys);
                } else if (dragging.column.colId.includes(ColumnTypes.DoorBulkQuantity)) {
                    if (toIndex <= itemDisplayProperty.size) {
                        const originalIndex = this.state.columnDefs.findIndex(
                            (col: ColDef) => col.colId === dragging.column.getId()
                        );
                        event.columnApi.moveColumn(dragging.column, originalIndex);
                    } else {
                        this.saveDoorsLayoutOnly(event.columnApi);
                    }
                }
            } else {
                if (dragging.column.colId.includes(ColumnTypes.DoorBulkQuantity)) {
                    const parentChildrenColumns = dragging.column.parent.children as Column[];
                    const totalCol = parentChildrenColumns.findIndex(
                        (c) =>
                            c.getColId().includes(ColumnTypes.AllocationDimensionTotal) ||
                            c.getColId().includes(ColumnTypes.ClassificationTotal)
                    );
                    if (totalCol > 0) {
                        const originalIndex = event.columnApi.getAllGridColumns().indexOf(parentChildrenColumns[0]);
                        event.columnApi.moveColumn(parentChildrenColumns[totalCol], originalIndex);
                    }
                }
                this.saveDoorsLayoutOnly(event.columnApi);
            }
        }

        this.setState({ dragging: {} });
    };

    private handleColumnGroupOpened = (e: ColumnGroupOpenedEvent) => {
        const { columnGroup } = e;
        this.props.onColumnGroupOpened(columnGroup.getGroupId(), columnGroup.isExpanded());
    };

    private getContextMenuItems = ({ node, column }) => {
        const {
            itemsId,
            onAddItem,
            onOpenDeleteConfirmation,
            itemDisplayProperty,
            onLinkProductToItem,
            editable,
            items,
            collectionId,
            copiedAllocation,
            copiedAllocationName,
            pasteAllocation,
            onPasteItems,
            bulkItems,
            copyItemsString,
            sizingAllocationEnabled,
            detailFields,
            onExportToExcel,
            allowDuplicateProduct,
            allocationBagRef,
            onMutateBagValues,
            shopViewFields,
        } = this.props;

        if (bulkItems.size > 0) {
            return;
        }

        const clickingOnEmptyGridSpace = !node || node.data === null || node.data === undefined || !node.data.fields;
        const isDefaultRow = node?.data?.type === DEFAULT_VALUES_PINNED_ROW;
        const data = get(node, ["data"], {});
        const dataFields = get(node, ["data", "fields"], {});
        const productId = get(dataFields, ["_product_id", "string_value"]);
        const isDimensionColumn = [
            ColumnTypes.DoorBulkQuantity,
            ColumnTypes.AllocationDimensionTotal,
            ColumnTypes.AllocationDimension,
            ColumnTypes.AllDimensionTotal,
            ColumnTypes.ClassificationTotal,
        ].includes(column?.colDef?.type);
        const hasDataFields = Object.keys(dataFields).length > 0;
        const noteValue = get(dataFields, [`${notePrefix}${column?.colId}`, "string_value"]);
        const shouldNoteMenuItemVisible = !isDimensionColumn && hasDataFields && !node.isRowPinned() && editable;

        let itemCount = 0;
        try {
            itemCount = JSON.parse(copyItemsString).length;
        } catch (e) {
            // deliberately left blank
        }

        // Just allow copy in context menu when right-clicking default row
        if (isDefaultRow) {
            return [...(!clickingOnEmptyGridSpace ? ["copy"] : [])];
        }

        let actions = [
            // eslint-disable-next-line no-constant-condition
            ...(allowDuplicateProduct
                ? [
                      {
                          name: formatMessage("Clone Item"),
                          disabled: !editable || clickingOnEmptyGridSpace,
                          action: () => {
                              onAddItem({
                                  itemsId,
                                  attributes: {
                                      ...data,
                                      fields: {
                                          ...dataFields,
                                          id: { string_value: uuid() },
                                      },
                                  },
                                  addType: "clone",
                                  index: this.getRowIndexById(dataFields.id.string_value) + 1,
                                  collectionId,
                              });
                          },
                          icon: "<span class='ag-icon' />",
                      },
                  ]
                : []),
            {
                name: formatMessage("Clone Item as SMU"),
                disabled: !editable || clickingOnEmptyGridSpace,
                action: () => {
                    delete dataFields._product_id;
                    delete dataFields._product_revision;
                    const oldId = dataFields.id.string_value;
                    const newId = uuid();
                    const attributes = {
                        ...data,
                        fields: {
                            ...dataFields,
                            id: { string_value: newId },
                        },
                    };
                    const colorField = amphibiousToJS(shopViewFields?.is_color?.field);
                    if (colorField?.shop_view?.is_color) {
                        attributes.fields[colorField.field_name] = {
                            string_value: "blank",
                        };
                    }
                    onAddItem({
                        itemsId,
                        attributes,
                        addType: "cloneSMU",
                        index: this.getRowIndexById(dataFields.id.string_value) + 1,
                        collectionId,
                    });
                    if (allocationBagRef) {
                        Object.values(AllocationBagType)
                            .filter((v) => typeof v === "number")
                            .forEach((bagType: AllocationBagType) => {
                                const bagKey = [oldId, bagType].join("#");
                                if (!allocationBagRef.allocationBag.has(bagKey)) {
                                    return;
                                }
                                const allocationBag = allocationBagRef.allocationBag.getIn(
                                    [bagKey, "map", "fields"],
                                    {}
                                );
                                allocationBag.forEach((value, key) => {
                                    const dimensionValues = key.split(BAG_VALUE_PARAMS_SEPARATOR);
                                    const quantity = +value.get("string_value", 0);
                                    onMutateBagValues({ bagType, rowId: newId, dimensionValues, quantity });
                                });
                            });
                    }

                    global.analytics.track(AssortmentShoppingActions.SmuCloned, {
                        userType: me.account_type,
                    });
                },
                icon: "<span class='ag-icon' />",
            },
            itemCount > 0 && {
                name: itemCount === 1 ? formatMessage("Paste Item") : formatMessage("Paste Items"),
                disabled: !editable,
                action: onPasteItems,
            },
            {
                name: formatMessage("Remove Item"),
                disabled: !editable || clickingOnEmptyGridSpace,
                action: () => {
                    onOpenDeleteConfirmation(itemsId, [dataFields.id.string_value]);
                },
                icon: `<span data-test="${DATATEST.ASSORTMENTS.DIALOG.DELETE}-${editable}" class="ag-icon ag-icon-cross" />`,
            },
            ...(!clickingOnEmptyGridSpace ? ["copy"] : []),
            ...(!clickingOnEmptyGridSpace && productId && allowDuplicateProduct
                ? [
                      {
                          name: formatMessage("Add Product Again"),
                          disabled: !editable,
                          action: () => {
                              onAddItem({
                                  itemsId,
                                  attributes: {
                                      fields: {
                                          id: { string_value: uuid() },
                                          product_id: { string_value: productId },
                                      },
                                  },
                                  index: this.getRowIndexById(dataFields.id.string_value) + 1,
                                  productId,
                                  collectionId,
                                  skipScroll: true,
                              });
                          },
                      },
                  ]
                : []),
            ...(!clickingOnEmptyGridSpace
                ? [
                      {
                          name: formatMessage("Link Product"),
                          disabled: !editable,
                          action: () => {
                              onLinkProductToItem(dataFields.id.string_value);
                          },
                      },
                  ]
                : []),
            ...(shouldNoteMenuItemVisible && !noteValue
                ? [
                      {
                          name: formatMessage("Insert Note"),
                          action: () => {
                              this.setState({
                                  requestedItemId: dataFields.id.string_value,
                                  requestedColumnId: column?.colId,
                                  selectedNode: node,
                              });
                              this.gridApi.redrawRows({ rowNodes: [node] });
                          },
                      },
                  ]
                : []),
            ...(shouldNoteMenuItemVisible && noteValue
                ? [
                      {
                          name: formatMessage("Clear Note"),
                          action: () => {
                              this.handleUpdateNote({
                                  itemId: data.fields.id.string_value,
                                  colId: column?.colId,
                                  value: "",
                                  node,
                              });
                          },
                      },
                  ]
                : []),
            ...(!this.props.isServerRollup
                ? [
                      "separator",
                      {
                          // Note: This automatically exports as .xlsx as of ag-grid v20
                          name: formatMessage("Excel Export"),
                          disabled: !itemDisplayProperty,
                          action: () => {
                              onExportToExcel();
                          },
                      },
                  ]
                : []),
        ];

        if (sizingAllocationEnabled && productId) {
            const isLegacyBag = !this.props.allocationBagRef;
            const rowId = get(node.data, Item.FIELDS.ID);
            const allocations = isLegacyBag
                ? get(node, ["data", ...Item.ALLOCATIONS])
                : getBagValuesByRowId(AllocationBagType.allocations, this.props.allocationBagRef.allocationBag, rowId);
            const bulks = isLegacyBag
                ? get(node, ["data", ...Item.BULKS])
                : getBagValuesByRowId(AllocationBagType.bulks, this.props.allocationBagRef.allocationBag, rowId);
            const hasAllocation = isLegacyBag ? !!allocations : allocations.size > 0;
            actions = [
                ...actions,
                {
                    name: formatMessage("Copy Allocation"),
                    disabled: !editable || !hasAllocation,
                    action: () => {
                        if (allocations) {
                            const isTitle = get(node, ["data", ...detailFields.is_title.path]);
                            const allocationName =
                                isTitle && isTitle.length > 8 ? `${isTitle.slice(0, 8)}...` : isTitle;
                            this.props.copyAllocation(
                                allocations,
                                allocationName,
                                get(node.data, ProductDisplayFieldPaths.FIELDS.SIZES),
                                bulks
                            );
                        } else {
                            this.props.onSnackbarAdd({ message: "There is no allocation saved to this row." });
                        }
                    },
                },
                {
                    name: copiedAllocationName
                        ? formatMessage(`Paste Allocation from ${copiedAllocationName}`)
                        : formatMessage("Paste Allocation"),
                    disabled: !copiedAllocation || !editable,
                    action: () => {
                        const rowIndex = this.getRowIndexById(dataFields.id.string_value);
                        const item = items.get(rowIndex);
                        pasteAllocation(item, rowIndex);
                    },
                },
            ];
        }

        return actions;
    };

    private getIndexById = (items, id) => {
        return (items as List<any>).findIndex((value) => {
            return value.getIn(["fields", "id", "string_value"]) === id;
        });
    };

    private handleDragHandleMouseUp = (e) => {
        // If dragged row is ever released outside grid, first check if target is .ag-icon.
        // If yes, move row back to pre-drag index
        if (this.state.isRowDraggedOutside && e.target.classList.contains("ag-icon")) {
            this.debouncedSubscribeToItemUpdates();
            this.setGridRowData(this.props.items);
            this.draggingItems = this.props.items;
        }

        // In case drag is released inside grid, make this `undefined` that means no row is being dragged.
        this.setState({
            isRowDraggedOutside: undefined,
        });

        // Remove listener that was attached `onRowDragLeave`. This has no effect if no listener was attached to begin with.
        window.document.body.removeEventListener("mouseup", this.handleDragHandleMouseUp);
    };

    private handleRowDragStart = (params) => {
        const {
            node: { id },
        } = params;
        this.debouncedSubscribeToItemUpdates.cancel();
        this.props.onUnsubscribeToItemsUpdates();

        // `onRowDragStart` is triggered on both drag start AND drag re-enters grid.
        this.setState({
            isRowDraggedOutside: false,
        });

        // if already dragging
        if (params.vDirection) {
            return;
        }

        const indexWithoutFilters = this.getIndexById(this.props.items, id);
        this.draggingItems = this.props.items;

        this.dragIndex = indexWithoutFilters !== -1 ? indexWithoutFilters : params.overIndex;
        process.nextTick(() => {
            // need to come after an internal call
            if (
                params.node.data &&
                params.node.data.fields &&
                params.node.data.fields.description &&
                params.node.data.fields.description.string_value
            ) {
                const $el = document.querySelector(".ag-dnd-ghost-label");
                if ($el) {
                    $el.textContent = params.node.data.fields.description.string_value;
                }
            } else if (
                params.node.data &&
                params.node.data.fields &&
                params.node.data.fields.vpn &&
                params.node.data.fields.vpn.string_value
            ) {
                const $el = document.querySelector(".ag-dnd-ghost-label");
                if ($el) {
                    $el.textContent = params.node.data.fields.vpn.string_value;
                }
            } else {
                const $el = document.querySelector(".ag-dnd-ghost-label");
                if ($el) {
                    $el.textContent = "";
                }
            }
        });
    };

    private handleRowDragMove = debounce(
        (params) => {
            const { node, overNode } = params;
            if (this.dragIndex !== undefined && overNode && node.id !== overNode.id) {
                const fromIndex = this.getIndexById(this.draggingItems, node.id);
                const indexWithoutFilters = this.getIndexById(this.draggingItems, overNode.id);
                // Note: If you drag a row past the last row, overIndex will be -1
                const destinationIndex = adjustDestinationIndex({
                    source: fromIndex,
                    proposedDestination: indexWithoutFilters,
                    totalLength: this.draggingItems.size,
                });

                this.draggingToIndex = destinationIndex;

                this.draggingItems = arrayMove(this.draggingItems, fromIndex, destinationIndex);
                const draggedItemsJS = this.getRowData(this.draggingItems, this.props.allocationBagRef?.allocationBag);

                !this.props.isServerRollup && this.gridApi.setRowData(draggedItemsJS);
            }
        },
        // 5 is sweet spot. Too big results in laggy-effect, too small results in same frenzy up-down effect.
        5
    );

    private handleRowDragEnd = ({ node: { id } }) => {
        this.props.onSubscribeToItemsUpdates();
        if (this.dragIndex !== undefined && this.draggingToIndex !== undefined) {
            this.props.onMoveItem(this.props.itemsId, this.dragIndex, this.draggingToIndex, id);
            this.dragIndex = undefined;
            this.draggingItems = undefined;
            this.draggingToIndex = undefined;
        }

        // `onRowDragEnd` won't trigger when drag is released outside grid.
        // In case drag is released inside grid, make this `undefined` that means no row is being dragged.
        this.setState({
            isRowDraggedOutside: undefined,
        });
    };

    private handleRowDragLeave = (params) => {
        // `onRowDragLeave` triggers when row is dragged outside grid.
        // We want to revert the dragged row to original index, so we set `isRowDraggedOutside=true` as flag to determine if we need to revert on `mouseup`.
        this.setState({
            isRowDraggedOutside: true,
        });
        window.document.body.addEventListener("mouseup", this.handleDragHandleMouseUp);
    };

    private handleRowSelectionChange = () => {
        this.props.onBulkItemsSet?.(this.getSelectedRowIds());
    };

    private handleToolPanelVisibleChanged = (event: ToolPanelVisibleChangedEvent) => {
        const { onToolPanelVisibleChanged } = this.props;
        const { lastToolpanelOpened } = this.state;
        const toolpanelOpened = event.api.getOpenedToolPanel();
        onToolPanelVisibleChanged(event);

        if (this.isGridTileView && (!toolpanelOpened || !lastToolpanelOpened)) {
            // Reset tileView when Close or Open the ToolPanel to recalculate the rows
            if (this.isGridTileView) {
                setTimeout(() => {
                    this.calculateGridTileParams(event.columnApi);
                    this.refreshTileViewAndPersistGroupsState();
                }, 100);
            }
        }
        this.setState({ lastToolpanelOpened: toolpanelOpened });
    };

    private isExternalFilterPresent = () => {
        const { searchTokens } = this.state;
        const { filterAssortmentByBrand, filterAssortmentBySMU, gridParams, customFilteredItems } = this.props;
        const dimensionFilters = getDimensionFilters(gridParams);
        const attributeFilters = getAttributeFilters(gridParams);

        return (
            this.props.collectionId &&
            (searchTokens.length > 0 ||
                !!filterAssortmentByBrand ||
                (filterAssortmentBySMU && filterAssortmentBySMU !== "all") ||
                !!dimensionFilters.length ||
                !!Object.keys(attributeFilters || {}).length ||
                customFilteredItems?.size > 0)
        );
    };

    private doesExternalFilterPass = (node) => {
        const { searchTokens } = this.state;
        const {
            filterAssortmentByBrand,
            filterAssortmentBySMU,
            gridParams,
            doors,
            dimensionsPanelApplied,
            allocationDimensions,
            customFilteredItems,
            allocationBagRef,
        } = this.props;

        // @ts-ignore columnModel is private
        const columns = this.columnApi.columnModel.getAllColumnsForQuickFilter();
        const dimensionFilters = getDimensionFilters(gridParams);
        const attributeFilters = getAttributeFilters(gridParams);
        const { attribute, attributeHiddenValues } = legacy_parseDimensionParams(dimensionsPanelApplied);

        if (customFilteredItems && !customFilteredItems.has(amphibiousGetIn(node.data, Item.FIELDS.ID))) {
            return false;
        }

        if (dimensionFilters.length || Object.keys(attributeFilters || {}).length) {
            if (
                !this.isDataMatchingDoorGrouping(
                    node,
                    dimensionFilters,
                    attributeFilters,
                    doors,
                    attribute,
                    attributeHiddenValues,
                    allocationDimensions,
                    allocationBagRef?.allocationBag
                )
            ) {
                return false;
            }
        }

        if (searchTokens.length) {
            if (!this.isDataMatchingSearchTerm(node, searchTokens, columns)) {
                return false;
            }
        }
        if (filterAssortmentByBrand) {
            if (!this.isDataMatchingBrandFilter(node, filterAssortmentByBrand)) {
                return false;
            }
        }
        if (filterAssortmentBySMU) {
            if (!this.isDataMatchingSMUFilter(node, filterAssortmentBySMU)) {
                return false;
            }
        }
        return true;
    };

    /**
     * Determine if row should be displayed by getting total for row with filters
     */
    private isDataMatchingDoorGrouping = (
        node: any,
        dimensionFilters: string[][],
        attributeFilters: IAttributeFilters,
        doors: IDoor[],
        activeClassification: string,
        hiddenClassificationValues: string[],
        allocationDimensions: IAllocationDimension[],
        allocationBag: AllocationBag
    ) =>
        !!doors
            .filter((door) =>
                isDoorInClassification(door, {
                    activeClassification,
                    hiddenClassificationValues,
                    attributeFilters,
                })
            )
            .reduce(
                (sum, door) =>
                    sum +
                    getBulkUnitsOverDimensions(
                        door.id,
                        node.data,
                        allocationDimensions,
                        dimensionFilters,
                        allocationBag
                    ),
                0
            );

    private isDataMatchingSearchTerm = (node, searchTokens, columns) => {
        const nonMatches = searchTokens.slice();

        for (const column of columns) {
            const part = this.getQuickFilterTextForColumn(column, node);
            if (part) {
                for (let i = 0; i < nonMatches.length; i++) {
                    if (part.indexOf(nonMatches[i]) >= 0) {
                        nonMatches.splice(i, 1);
                        i--;
                        if (nonMatches.length === 0) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    };

    private isDataMatchingBrandFilter = (node, brandFilter) => {
        const brandId = node.data && get(node.data, "fields._brand_id.string_value");
        return brandId === brandFilter;
    };

    private isDataMatchingSMUFilter = (node, SMUFilter) => {
        switch (SMUFilter) {
            case SMU_FILTER.SMUOnly:
                return node.data && !get(node.data, `fields.${FILTER_KEY.ProductID}.string_value`); // Note: Empty string or undefined
            case SMU_FILTER.ExcludeSMU:
                return node.data && get(node.data, `fields.${FILTER_KEY.ProductID}.string_value`);
            case SMU_FILTER.All:
            default:
                return true;
        }
    };

    private getQuickFilterTextForColumn(column: Column, rowNode): string {
        if (column.getColId().includes(ColumnTypes.DoorBulkQuantity)) {
            return null;
        }
        const value = (this.gridApi as any).valueService.getValue(column, rowNode);

        let valueAfterCallback: any;
        const colDef = column.getColDef();
        const { gridContext } = this.state;
        if (column.getColDef().getQuickFilterText) {
            const params = {
                value,
                node: rowNode,
                data: rowNode.data,
                column,
                colDef,
                context: gridContext,
            };
            valueAfterCallback = column.getColDef().getQuickFilterText({
                ...params,
                api: this.gridApi,
                columnApi: this.columnApi,
            });
        } else {
            valueAfterCallback = value;
        }

        if (valueAfterCallback && valueAfterCallback !== "") {
            return valueAfterCallback.toString().toUpperCase();
        } else {
            return null;
        }
    }

    private generateFocusRowsFromFocusMaps(previousFocusMap, currentFocusMap) {
        return currentFocusMap
            .merge(previousFocusMap)
            .toKeyedSeq()
            .reduce((accumulator, value, key) => {
                if (!key) {
                    return accumulator;
                }
                const rowId = key.split(".")[0];
                const rowNode = this.gridApi.getRowNode(rowId);

                if (!rowNode) {
                    return accumulator;
                }

                accumulator = accumulator.concat(rowNode);

                return accumulator;
            }, []);
    }

    private handleViewErrorLog = (location, router, params) => {
        const { query } = location;
        router.push({
            pathname: location.pathname,
            query: {
                ...query,
                cartView: params.validationLogView,
                validationRowId: params.validationRowId,
                errorStatus: params.errorStatus,
            },
        });
    };

    private handleResetRowHeight = () => {
        this.gridApi.resetRowHeights();
    };

    private getNoteValue = (itemId: string, colId: string, collectionId: string) => {
        let columnId = colId;
        if (this.props.isServerRollup) {
            columnId = collectionId ? buildServerRollupRowNodeId(itemId, collectionId) : itemId;
        }

        const item = this.gridApi.getRowNode(itemId)?.data;
        return get(item, ["fields", `${notePrefix}${columnId}`, "string_value"]);
    };

    /**
     * This is a unique function that is used to compute the metric column.
     * Why here? Because the required params to compute are too specific to this component and it will polute other components to accept these params as their props.
     * TODO: extract this to a separate util function and add UT
     *
     * @param selfColumnId - The column id to show the value. This can be ANY metric column that will show value of other metric. Particular use case: collapsed mode
     * @param metricKind - Metric kind to get
     * @param rowNodes - Row nodes to get the value from
     */
    public getMetricValuesForRows = (selfColumnId: string, metricKind: MetricsKind, rowNodes: RowNode[]) => {
        const siblingMetricColumn = getReferenceColId(selfColumnId, metricKind);
        const siblingColumn = this.columnApi.getColumn(siblingMetricColumn);
        // If there is sibling column, EASY! Just use the value from sibling column
        if (siblingColumn) {
            return rowNodes.map((rowNode) => this.gridApi.getValue(siblingMetricColumn, rowNode));
        }

        const { filteredDeliveriesForAssortmentSet, filteredDoorsForAssortmentSet } = getRequiredMetricParams({
            gridParams: this.props.gridParams,
            deliveries: this.props.dates,
            doors: this.props.doors,
        });

        const column = this.columnApi.getColumn(selfColumnId);

        if (!column) {
            return undefined;
        }

        const colDef = column.getColDef();
        const syntheticBaseGetterParams: ValueGetterParams = {
            column,
            colDef,
            api: this.gridApi,
            columnApi: this.columnApi,
            context: this.state.gridContext,
            getValue: function (_field: string) {
                throw new Error("Not implemented in synthetic getter");
            },

            // Below props will be updated based on the row node
            node: undefined,
            data: undefined,
        };

        const getterFn = getMetricValueGetterFn({
            rootGridApi: this.gridApi,
            filteredDeliveriesForAssortmentSet,
            filteredDoorsForAssortmentSet,
            metric: this.props.dimensionsPanelApplied.metrics.values.find((metric) => metric.kind === metricKind),

            // This is assuming `colDef` is a metric column which will have `cellRendererParams`
            columnDimensionIds: colDef.cellRendererParams.dimensionIds,
        });

        return rowNodes.map((rowNode) => {
            const syntheticGetterParams = {
                ...syntheticBaseGetterParams,
                node: rowNode,
                data: rowNode.data,
            };

            return getterFn(syntheticGetterParams);
        });
    };

    /**
     * A collection of functions which are called on the component unmount.
     * All debounced methods should be canceled to prevent them from being called after the component unmount.
     * A sign of the violation is a warning in react development mode that f.x. `setState was called after the component is unmounted`.
     */
    private teardownActions: AnyFunction[] = [
        this.handleSaveGridLayout.cancel,
        this.handleColumnRowGroupChanged.cancel,
        this.handleColumnValueChanged.cancel,
        this.handleColumnPivotChanged.cancel,
        this.debouncedSubscribeToItemUpdates.cancel,
        this.debouncedUpdateTileViewAfterResizeWindow.cancel,
        this.applyDebouncedFilterModel.cancel,
        this.generateDefaultColumnDefsProps.cancel,
        this.refreshPinnedRows.cancel,
        this.resetPinnedRows.cancel,
        this.debouncedHandleFilterChanged.cancel,
        this.handleModelUpdated.cancel,
        this.handleRowDragMove.cancel,
    ];
}
